import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { usePermissions } from '../../utils/permissions';
import {
  Box, Container, Typography, Button, TextField, Dialog, DialogTitle, DialogContent,
  DialogActions, IconButton, Alert, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination, Fab, Card, CardContent, Snackbar, useTheme,
  alpha, Fade, LinearProgress, InputAdornment, Grid, Divider, Tooltip, Chip,
  FormControl, InputLabel, Select, MenuItem, FormControlLabel, Checkbox
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Close as CloseIcon, Search as SearchIcon, Input as InputIcon,
  ArrowBack as ArrowBackIcon, Check as CheckIcon, Cancel as CancelIcon,
  Verified as VerifiedIcon, CheckCircle as CheckCircleIcon,
  Visibility as VisibilityIcon, AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon, Description as DescriptionIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';

const CATEGORIES = [
  { id: 'raw_materials', name: 'Chemicals' },
  { id: 'caps', name: 'Caps' },
  { id: 'bottles', name: 'Bottles' },
  { id: 'labels', name: 'Labels' },
  { id: 'supplies', name: 'Supplies' }
];

const ImportsPage = () => {
  const { companyId, category } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const { hasPermission } = usePermissions();
  
  // State management
  const [imports, setImports] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [companyName, setCompanyName] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [importToDelete, setImportToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  
  // Approval dialog states
  const [approvalDialogOpen, setApprovalDialogOpen] = useState(false);
  const [approvalImport, setApprovalImport] = useState(null);
  const [signatureName, setSignatureName] = useState('');
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // View dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewImport, setViewImport] = useState(null);
  
  // Transaction autofill dialog states (for chemicals)
  const [transactionDialogOpen, setTransactionDialogOpen] = useState(false);
  const [transactions, setTransactions] = useState([]);
  const [loadingTransactions, setLoadingTransactions] = useState(false);
  const [transactionSearchTerm, setTransactionSearchTerm] = useState('');
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  
  // Transaction autofill dialog states (for caps, bottles, labels, supplies)
  const [capsTransactionDialogOpen, setCapsTransactionDialogOpen] = useState(false);
  const [capsTransactions, setCapsTransactions] = useState([]);
  const [loadingCapsTransactions, setLoadingCapsTransactions] = useState(false);
  const [capsTransactionSearchTerm, setCapsTransactionSearchTerm] = useState('');
  const [filteredCapsTransactions, setFilteredCapsTransactions] = useState([]);
  
  const [bottlesTransactionDialogOpen, setBottlesTransactionDialogOpen] = useState(false);
  const [bottlesTransactions, setBottlesTransactions] = useState([]);
  const [loadingBottlesTransactions, setLoadingBottlesTransactions] = useState(false);
  const [bottlesTransactionSearchTerm, setBottlesTransactionSearchTerm] = useState('');
  const [filteredBottlesTransactions, setFilteredBottlesTransactions] = useState([]);
  
  const [labelsTransactionDialogOpen, setLabelsTransactionDialogOpen] = useState(false);
  const [labelsTransactions, setLabelsTransactions] = useState([]);
  const [loadingLabelsTransactions, setLoadingLabelsTransactions] = useState(false);
  const [labelsTransactionSearchTerm, setLabelsTransactionSearchTerm] = useState('');
  const [filteredLabelsTransactions, setFilteredLabelsTransactions] = useState([]);
  
  const [suppliesTransactionDialogOpen, setSuppliesTransactionDialogOpen] = useState(false);
  const [suppliesTransactions, setSuppliesTransactions] = useState([]);
  const [loadingSuppliesTransactions, setLoadingSuppliesTransactions] = useState(false);
  const [suppliesTransactionSearchTerm, setSuppliesTransactionSearchTerm] = useState('');
  const [filteredSuppliesTransactions, setFilteredSuppliesTransactions] = useState([]);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(CATEGORIES);
  const [filteredImports, setFilteredImports] = useState([]);
  
  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Form data
  const [formData, setFormData] = useState({
    id: '',
    serial_number: '',
    date_of_receipt: dayjs(),
    purchase_order_no: '',
    invoice_number: '',
    supplier_vendor_name: '',
    material_name: '',
    material_description: '',
    batch_no: '',
    lot_no: '',
    coa_received: false,
    manufacturing_date: null,
    expiry_date: null,
    container_type: '',
    container_count: '',
    unit_of_measure: '',
    quantity_per_container: '',
    quantity_ordered: '',
    quantity_received: '',
    location: '',
    sampling_done: false,
    label_status: 'pending',
    qty_issued_to_production: '',
    date_of_issue: null,
    remarks: '',
    invoice_file_path: '',
    invoice_file_name: '',
    invoice_file_type: '',
    invoice_file_size: null,
    po_file_path: '',
    po_file_name: '',
    po_file_type: '',
    po_file_size: null,
    coa_file_path: '',
    coa_file_name: '',
    coa_file_type: '',
    coa_file_size: null,
    msds_file_path: '',
    msds_file_name: '',
    msds_file_type: '',
    msds_file_size: null,
    bol_file_path: '',
    bol_file_name: '',
    bol_file_type: '',
    bol_file_size: null
  });

  // Document file states
  const [invoiceFile, setInvoiceFile] = useState(null);
  const [poFile, setPoFile] = useState(null);
  const [coaFile, setCoaFile] = useState(null);
  const [msdsFile, setMsdsFile] = useState(null);
  const [bolFile, setBolFile] = useState(null);
  const [uploadingInvoice, setUploadingInvoice] = useState(false);

  // Helper function to copy a file from one bucket to another
  const copyFileToBucket = async (sourceBucket, sourceFilePath, sourceFileName, sourceFileType, sourceFileSize) => {
    try {
      if (!sourceFilePath) return null;

      // Download the file from source bucket
      const { data: fileBlob, error: downloadError } = await supabase.storage
        .from(sourceBucket)
        .download(sourceFilePath);

      if (downloadError) throw downloadError;

      // Create a new File object from the blob
      const file = new File(
        [fileBlob], 
        sourceFileName || 'document.pdf',
        { type: sourceFileType || 'application/pdf' }
      );

      // Upload to import-documents bucket
      const fileExt = (sourceFileName || 'document.pdf').split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('import-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      return {
        file_path: filePath,
        file_name: sourceFileName || 'document.pdf',
        file_type: sourceFileType || 'application/pdf',
        file_size: sourceFileSize || file.size
      };
    } catch (error) {
      console.error('Error copying file:', error);
      throw error;
    }
  };

  const fetchCompanyData = async () => {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('name')
        .eq('id', companyId)
        .single();
      if (error) throw error;
      setCompanyName(data?.name || 'Company');
    } catch (err) {
      console.error('Error fetching company:', err);
      setCompanyName('Company Name');
    }
  };

  const fetchImports = async () => {
    if (!selectedCategory) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('imports')
        .select('*')
        .eq('company_id', companyId)
        .eq('category', selectedCategory.id)
        .order('serial_number', { ascending: false });
      if (error) throw error;
      setImports(data || []);
    } catch (err) {
      console.error('Error fetching imports:', err);
      setError('Failed to load imports.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRawMaterialTransactions = async () => {
    setLoadingTransactions(true);
    try {
      const { data, error } = await supabase
        .from('raw_materials_transactions')
        .select(`
          *,
          raw_material:raw_materials(id, name, primary_unit_type, primary_unit_quantity, primary_unit_measure),
          location:locations!location_id(id, name),
          supplier:suppliers(id, name)
        `)
        .eq('company_id', companyId)
        .eq('transaction_type', 'purchase')
        .order('transaction_date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setTransactions(data || []);
      setFilteredTransactions(data || []);
    } catch (err) {
      console.error('Error fetching transactions:', err);
      setSnackbar({ open: true, message: 'Failed to load transactions', severity: 'error' });
    } finally {
      setLoadingTransactions(false);
    }
  };

  const handleOpenTransactionDialog = () => {
    setTransactionDialogOpen(true);
    fetchRawMaterialTransactions();
  };

  const handleSelectTransaction = async (transaction) => {
    try {
      let documentData = {
        invoice_file_path: '',
        invoice_file_name: '',
        invoice_file_type: '',
        invoice_file_size: null,
        po_file_path: '',
        po_file_name: '',
        po_file_type: '',
        po_file_size: null,
        coa_file_path: '',
        coa_file_name: '',
        coa_file_type: '',
        coa_file_size: null,
        msds_file_path: '',
        msds_file_name: '',
        msds_file_type: '',
        msds_file_size: null,
        bol_file_path: '',
        bol_file_name: '',
        bol_file_type: '',
        bol_file_size: null
      };

      setSnackbar({ open: true, message: 'Copying documents...', severity: 'info' });
      let copiedFiles = [];

      // Copy invoice file
      if (transaction.invoice_file_path) {
        try {
          const invoiceResult = await copyFileToBucket(
            'raw-materials-transactions-invoices',
            transaction.invoice_file_path,
            transaction.invoice_file_name,
            transaction.invoice_file_type,
            transaction.invoice_file_size
          );
          if (invoiceResult) {
            documentData.invoice_file_path = invoiceResult.file_path;
            documentData.invoice_file_name = invoiceResult.file_name;
            documentData.invoice_file_type = invoiceResult.file_type;
            documentData.invoice_file_size = invoiceResult.file_size;
            copiedFiles.push('Invoice');
          }
        } catch (err) {
          console.error('Error copying invoice:', err);
        }
      }

      // Copy PO file
      if (transaction.po_file_path) {
        try {
          const poResult = await copyFileToBucket(
            'raw-material-inventory-po',
            transaction.po_file_path,
            transaction.po_file_name,
            'application/pdf',
            transaction.po_file_size
          );
          if (poResult) {
            documentData.po_file_path = poResult.file_path;
            documentData.po_file_name = poResult.file_name;
            documentData.po_file_type = poResult.file_type;
            documentData.po_file_size = poResult.file_size;
            copiedFiles.push('PO');
          }
        } catch (err) {
          console.error('Error copying PO:', err);
        }
      }

      // Copy COA file
      if (transaction.coa_file_path) {
        try {
          const coaResult = await copyFileToBucket(
            'raw-materials-inventory-coa',
            transaction.coa_file_path,
            transaction.coa_file_name,
            'application/pdf',
            transaction.coa_file_size
          );
          if (coaResult) {
            documentData.coa_file_path = coaResult.file_path;
            documentData.coa_file_name = coaResult.file_name;
            documentData.coa_file_type = coaResult.file_type;
            documentData.coa_file_size = coaResult.file_size;
            copiedFiles.push('COA');
          }
        } catch (err) {
          console.error('Error copying COA:', err);
        }
      }

      // Copy MSDS file
      if (transaction.msds_file_path) {
        try {
          const msdsResult = await copyFileToBucket(
            'raw-materials-inventory-msds',
            transaction.msds_file_path,
            transaction.msds_file_name,
            'application/pdf',
            transaction.msds_file_size
          );
          if (msdsResult) {
            documentData.msds_file_path = msdsResult.file_path;
            documentData.msds_file_name = msdsResult.file_name;
            documentData.msds_file_type = msdsResult.file_type;
            documentData.msds_file_size = msdsResult.file_size;
            copiedFiles.push('MSDS');
          }
        } catch (err) {
          console.error('Error copying MSDS:', err);
        }
      }

      // Copy BOL file
      if (transaction.bol_file_path) {
        try {
          const bolResult = await copyFileToBucket(
            'raw-material-inventory-bol',
            transaction.bol_file_path,
            transaction.bol_file_name,
            'application/pdf',
            transaction.bol_file_size
          );
          if (bolResult) {
            documentData.bol_file_path = bolResult.file_path;
            documentData.bol_file_name = bolResult.file_name;
            documentData.bol_file_type = bolResult.file_type;
            documentData.bol_file_size = bolResult.file_size;
            copiedFiles.push('BOL');
          }
        } catch (err) {
          console.error('Error copying BOL:', err);
        }
      }

      // Autofill form data from transaction
      setFormData(prev => ({
        ...prev,
        purchase_order_no: transaction.po_number || '',
        invoice_number: transaction.invoice_number || '',
        supplier_vendor_name: transaction.supplier?.name || '',
        material_name: transaction.raw_material?.name || '',
        manufacturing_date: transaction.manufactured_date ? dayjs(transaction.manufactured_date) : null,
        expiry_date: transaction.expiry_date ? dayjs(transaction.expiry_date) : null,
        container_type: transaction.raw_material?.primary_unit_type || '',
        container_count: Math.abs(transaction.unit_quantity) || '',
        unit_of_measure: transaction.raw_material?.primary_unit_measure || '',
        quantity_per_container: Math.abs(transaction.total_quantity) || '',
        quantity_ordered: Math.abs(transaction.total_quantity) || '',
        quantity_received: Math.abs(transaction.total_quantity) || '',
        location: transaction.location?.name || '',
        coa_received: transaction.coa_file_path ? true : false,
        batch_no: transaction.batch_number || '',
        lot_no: transaction.lot_number || '',
        remarks: transaction.notes || '',
        ...documentData
      }));
      
      setTransactionDialogOpen(false);
      const message = copiedFiles.length > 0
        ? `Form autofilled with ${copiedFiles.join(', ')} document${copiedFiles.length > 1 ? 's' : ''}`
        : 'Form autofilled from transaction';
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (err) {
      console.error('Error autofilling from transaction:', err);
      setSnackbar({ open: true, message: 'Error autofilling form', severity: 'error' });
    }
  };

  // Caps transactions autofill
  const fetchCapsTransactions = async () => {
    setLoadingCapsTransactions(true);
    try {
      const { data, error } = await supabase
        .from('caps_transactions')
        .select(`
          *,
          cap:caps(id, name),
          location:locations!location_id(id, name),
          supplier:suppliers(id, name)
        `)
        .eq('company_id', companyId)
        .eq('transaction_type', 'purchase')
        .order('transaction_date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setCapsTransactions(data || []);
      setFilteredCapsTransactions(data || []);
    } catch (err) {
      console.error('Error fetching caps transactions:', err);
      setSnackbar({ open: true, message: 'Failed to load caps transactions', severity: 'error' });
    } finally {
      setLoadingCapsTransactions(false);
    }
  };

  const handleOpenCapsTransactionDialog = () => {
    setCapsTransactionDialogOpen(true);
    fetchCapsTransactions();
  };

  const handleSelectCapsTransaction = async (transaction) => {
    try {
      let documentData = {
        invoice_file_path: '', invoice_file_name: '', invoice_file_type: '', invoice_file_size: null,
        po_file_path: '', po_file_name: '', po_file_type: '', po_file_size: null,
        coa_file_path: '', coa_file_name: '', coa_file_type: '', coa_file_size: null,
        msds_file_path: '', msds_file_name: '', msds_file_type: '', msds_file_size: null,
        bol_file_path: '', bol_file_name: '', bol_file_type: '', bol_file_size: null
      };

      setSnackbar({ open: true, message: 'Copying documents...', severity: 'info' });
      let copiedFiles = [];

      // Copy all document files
      if (transaction.invoice_file_path) {
        try {
          const result = await copyFileToBucket('caps-transactions-invoices', transaction.invoice_file_path, transaction.invoice_file_name, transaction.invoice_file_type, transaction.invoice_file_size);
          if (result) { Object.assign(documentData, { invoice_file_path: result.file_path, invoice_file_name: result.file_name, invoice_file_type: result.file_type, invoice_file_size: result.file_size }); copiedFiles.push('Invoice'); }
        } catch (err) { console.error('Error copying invoice:', err); }
      }
      if (transaction.po_file_path) {
        try {
          const result = await copyFileToBucket('caps-transactions-po', transaction.po_file_path, transaction.po_file_name, 'application/pdf', transaction.po_file_size);
          if (result) { Object.assign(documentData, { po_file_path: result.file_path, po_file_name: result.file_name, po_file_type: result.file_type, po_file_size: result.file_size }); copiedFiles.push('PO'); }
        } catch (err) { console.error('Error copying PO:', err); }
      }
      if (transaction.coa_file_path) {
        try {
          const result = await copyFileToBucket('caps-transactions-coa', transaction.coa_file_path, transaction.coa_file_name, 'application/pdf', transaction.coa_file_size);
          if (result) { Object.assign(documentData, { coa_file_path: result.file_path, coa_file_name: result.file_name, coa_file_type: result.file_type, coa_file_size: result.file_size }); copiedFiles.push('COA'); }
        } catch (err) { console.error('Error copying COA:', err); }
      }
      if (transaction.msds_file_path) {
        try {
          const result = await copyFileToBucket('caps-transactions-msds', transaction.msds_file_path, transaction.msds_file_name, 'application/pdf', transaction.msds_file_size);
          if (result) { Object.assign(documentData, { msds_file_path: result.file_path, msds_file_name: result.file_name, msds_file_type: result.file_type, msds_file_size: result.file_size }); copiedFiles.push('MSDS'); }
        } catch (err) { console.error('Error copying MSDS:', err); }
      }
      if (transaction.bol_file_path) {
        try {
          const result = await copyFileToBucket('caps-transactions-bol', transaction.bol_file_path, transaction.bol_file_name, 'application/pdf', transaction.bol_file_size);
          if (result) { Object.assign(documentData, { bol_file_path: result.file_path, bol_file_name: result.file_name, bol_file_type: result.file_type, bol_file_size: result.file_size }); copiedFiles.push('BOL'); }
        } catch (err) { console.error('Error copying BOL:', err); }
      }

      setFormData(prev => ({
        ...prev,
        purchase_order_no: transaction.po_number || '',
        invoice_number: transaction.invoice_number || '',
        supplier_vendor_name: transaction.supplier?.name || '',
        material_name: transaction.cap?.name || '',
        quantity_ordered: Math.abs(transaction.quantity) || '',
        quantity_received: Math.abs(transaction.quantity) || '',
        location: transaction.location?.name || '',
        batch_no: transaction.batch_number || '',
        lot_no: transaction.lot_number || '',
        remarks: transaction.notes || '',
        ...documentData
      }));
      
      setCapsTransactionDialogOpen(false);
      const message = copiedFiles.length > 0
        ? `Form autofilled with ${copiedFiles.join(', ')} document${copiedFiles.length > 1 ? 's' : ''}`
        : 'Form autofilled from transaction';
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (err) {
      console.error('Error autofilling from caps transaction:', err);
      setSnackbar({ open: true, message: 'Error autofilling form', severity: 'error' });
    }
  };

  // Bottles transactions autofill
  const fetchBottlesTransactions = async () => {
    setLoadingBottlesTransactions(true);
    try {
      const { data, error } = await supabase
        .from('bottle_transactions')
        .select(`
          *,
          bottle:bottles(id, name),
          location:locations!location_id(id, name),
          supplier:suppliers(id, name)
        `)
        .eq('company_id', companyId)
        .eq('transaction_type', 'purchase')
        .order('transaction_date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setBottlesTransactions(data || []);
      setFilteredBottlesTransactions(data || []);
    } catch (err) {
      console.error('Error fetching bottles transactions:', err);
      setSnackbar({ open: true, message: 'Failed to load bottles transactions', severity: 'error' });
    } finally {
      setLoadingBottlesTransactions(false);
    }
  };

  const handleOpenBottlesTransactionDialog = () => {
    setBottlesTransactionDialogOpen(true);
    fetchBottlesTransactions();
  };

  const handleSelectBottlesTransaction = async (transaction) => {
    try {
      let documentData = {
        invoice_file_path: '', invoice_file_name: '', invoice_file_type: '', invoice_file_size: null,
        po_file_path: '', po_file_name: '', po_file_type: '', po_file_size: null,
        coa_file_path: '', coa_file_name: '', coa_file_type: '', coa_file_size: null,
        msds_file_path: '', msds_file_name: '', msds_file_type: '', msds_file_size: null,
        bol_file_path: '', bol_file_name: '', bol_file_type: '', bol_file_size: null
      };

      setSnackbar({ open: true, message: 'Copying documents...', severity: 'info' });
      let copiedFiles = [];

      // Copy all document files
      if (transaction.invoice_file_path) {
        try {
          const result = await copyFileToBucket('bottles-transactions-invoices', transaction.invoice_file_path, transaction.invoice_file_name, transaction.invoice_file_type, transaction.invoice_file_size);
          if (result) { Object.assign(documentData, { invoice_file_path: result.file_path, invoice_file_name: result.file_name, invoice_file_type: result.file_type, invoice_file_size: result.file_size }); copiedFiles.push('Invoice'); }
        } catch (err) { console.error('Error copying invoice:', err); }
      }
      if (transaction.po_file_path) {
        try {
          const result = await copyFileToBucket('bottles-transactions-po', transaction.po_file_path, transaction.po_file_name, 'application/pdf', transaction.po_file_size);
          if (result) { Object.assign(documentData, { po_file_path: result.file_path, po_file_name: result.file_name, po_file_type: result.file_type, po_file_size: result.file_size }); copiedFiles.push('PO'); }
        } catch (err) { console.error('Error copying PO:', err); }
      }
      if (transaction.coa_file_path) {
        try {
          const result = await copyFileToBucket('bottles-transactions-coa', transaction.coa_file_path, transaction.coa_file_name, 'application/pdf', transaction.coa_file_size);
          if (result) { Object.assign(documentData, { coa_file_path: result.file_path, coa_file_name: result.file_name, coa_file_type: result.file_type, coa_file_size: result.file_size }); copiedFiles.push('COA'); }
        } catch (err) { console.error('Error copying COA:', err); }
      }
      if (transaction.msds_file_path) {
        try {
          const result = await copyFileToBucket('bottles-transactions-msds', transaction.msds_file_path, transaction.msds_file_name, 'application/pdf', transaction.msds_file_size);
          if (result) { Object.assign(documentData, { msds_file_path: result.file_path, msds_file_name: result.file_name, msds_file_type: result.file_type, msds_file_size: result.file_size }); copiedFiles.push('MSDS'); }
        } catch (err) { console.error('Error copying MSDS:', err); }
      }
      if (transaction.bol_file_path) {
        try {
          const result = await copyFileToBucket('bottles-transactions-bol', transaction.bol_file_path, transaction.bol_file_name, 'application/pdf', transaction.bol_file_size);
          if (result) { Object.assign(documentData, { bol_file_path: result.file_path, bol_file_name: result.file_name, bol_file_type: result.file_type, bol_file_size: result.file_size }); copiedFiles.push('BOL'); }
        } catch (err) { console.error('Error copying BOL:', err); }
      }

      setFormData(prev => ({
        ...prev,
        purchase_order_no: transaction.po_number || '',
        invoice_number: transaction.invoice_number || '',
        supplier_vendor_name: transaction.supplier?.name || '',
        material_name: transaction.bottle?.name || '',
        quantity_ordered: Math.abs(transaction.quantity) || '',
        quantity_received: Math.abs(transaction.quantity) || '',
        location: transaction.location?.name || '',
        batch_no: transaction.batch_number || '',
        lot_no: transaction.lot_number || '',
        remarks: transaction.notes || '',
        ...documentData
      }));
      
      setBottlesTransactionDialogOpen(false);
      const message = copiedFiles.length > 0
        ? `Form autofilled with ${copiedFiles.join(', ')} document${copiedFiles.length > 1 ? 's' : ''}`
        : 'Form autofilled from transaction';
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (err) {
      console.error('Error autofilling from bottles transaction:', err);
      setSnackbar({ open: true, message: 'Error autofilling form', severity: 'error' });
    }
  };

  // Labels transactions autofill
  const fetchLabelsTransactions = async () => {
    setLoadingLabelsTransactions(true);
    try {
      const { data, error} = await supabase
        .from('label_transactions')
        .select(`
          *,
          label:labels(id, name),
          location:locations!location_id(id, name),
          supplier:suppliers(id, name)
        `)
        .eq('company_id', companyId)
        .eq('transaction_type', 'purchase')
        .order('transaction_date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setLabelsTransactions(data || []);
      setFilteredLabelsTransactions(data || []);
    } catch (err) {
      console.error('Error fetching labels transactions:', err);
      setSnackbar({ open: true, message: 'Failed to load labels transactions', severity: 'error' });
    } finally {
      setLoadingLabelsTransactions(false);
    }
  };

  const handleOpenLabelsTransactionDialog = () => {
    setLabelsTransactionDialogOpen(true);
    fetchLabelsTransactions();
  };

  const handleSelectLabelsTransaction = async (transaction) => {
    try {
      let documentData = {
        invoice_file_path: '', invoice_file_name: '', invoice_file_type: '', invoice_file_size: null,
        po_file_path: '', po_file_name: '', po_file_type: '', po_file_size: null,
        coa_file_path: '', coa_file_name: '', coa_file_type: '', coa_file_size: null,
        msds_file_path: '', msds_file_name: '', msds_file_type: '', msds_file_size: null,
        bol_file_path: '', bol_file_name: '', bol_file_type: '', bol_file_size: null
      };

      setSnackbar({ open: true, message: 'Copying documents...', severity: 'info' });
      let copiedFiles = [];

      // Copy all document files
      if (transaction.invoice_file_path) {
        try {
          const result = await copyFileToBucket('labels-transactions-invoices', transaction.invoice_file_path, transaction.invoice_file_name, transaction.invoice_file_type, transaction.invoice_file_size);
          if (result) { Object.assign(documentData, { invoice_file_path: result.file_path, invoice_file_name: result.file_name, invoice_file_type: result.file_type, invoice_file_size: result.file_size }); copiedFiles.push('Invoice'); }
        } catch (err) { console.error('Error copying invoice:', err); }
      }
      if (transaction.po_file_path) {
        try {
          const result = await copyFileToBucket('labels-transactions-po', transaction.po_file_path, transaction.po_file_name, 'application/pdf', transaction.po_file_size);
          if (result) { Object.assign(documentData, { po_file_path: result.file_path, po_file_name: result.file_name, po_file_type: result.file_type, po_file_size: result.file_size }); copiedFiles.push('PO'); }
        } catch (err) { console.error('Error copying PO:', err); }
      }
      if (transaction.coa_file_path) {
        try {
          const result = await copyFileToBucket('labels-transactions-coa', transaction.coa_file_path, transaction.coa_file_name, 'application/pdf', transaction.coa_file_size);
          if (result) { Object.assign(documentData, { coa_file_path: result.file_path, coa_file_name: result.file_name, coa_file_type: result.file_type, coa_file_size: result.file_size }); copiedFiles.push('COA'); }
        } catch (err) { console.error('Error copying COA:', err); }
      }
      if (transaction.msds_file_path) {
        try {
          const result = await copyFileToBucket('labels-transactions-msds', transaction.msds_file_path, transaction.msds_file_name, 'application/pdf', transaction.msds_file_size);
          if (result) { Object.assign(documentData, { msds_file_path: result.file_path, msds_file_name: result.file_name, msds_file_type: result.file_type, msds_file_size: result.file_size }); copiedFiles.push('MSDS'); }
        } catch (err) { console.error('Error copying MSDS:', err); }
      }
      if (transaction.bol_file_path) {
        try {
          const result = await copyFileToBucket('labels-transactions-bol', transaction.bol_file_path, transaction.bol_file_name, 'application/pdf', transaction.bol_file_size);
          if (result) { Object.assign(documentData, { bol_file_path: result.file_path, bol_file_name: result.file_name, bol_file_type: result.file_type, bol_file_size: result.file_size }); copiedFiles.push('BOL'); }
        } catch (err) { console.error('Error copying BOL:', err); }
      }

      setFormData(prev => ({
        ...prev,
        purchase_order_no: transaction.po_number || '',
        invoice_number: transaction.invoice_number || '',
        supplier_vendor_name: transaction.supplier?.name || '',
        material_name: transaction.label?.name || '',
        quantity_ordered: Math.abs(transaction.quantity) || '',
        quantity_received: Math.abs(transaction.quantity) || '',
        location: transaction.location?.name || '',
        batch_no: transaction.batch_number || '',
        lot_no: transaction.lot_number || '',
        remarks: transaction.notes || '',
        ...documentData
      }));
      
      setLabelsTransactionDialogOpen(false);
      const message = copiedFiles.length > 0
        ? `Form autofilled with ${copiedFiles.join(', ')} document${copiedFiles.length > 1 ? 's' : ''}`
        : 'Form autofilled from transaction';
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (err) {
      console.error('Error autofilling from labels transaction:', err);
      setSnackbar({ open: true, message: 'Error autofilling form', severity: 'error' });
    }
  };

  // Supplies transactions autofill
  const fetchSuppliesTransactions = async () => {
    setLoadingSuppliesTransactions(true);
    try {
      const { data, error } = await supabase
        .from('supply_transactions')
        .select(`
          *,
          supply:supplies(id, name),
          location:locations!location_id(id, name),
          supplier:suppliers(id, name)
        `)
        .eq('company_id', companyId)
        .eq('transaction_type', 'purchase')
        .order('transaction_date', { ascending: false })
        .limit(100);
      
      if (error) throw error;
      setSuppliesTransactions(data || []);
      setFilteredSuppliesTransactions(data || []);
    } catch (err) {
      console.error('Error fetching supplies transactions:', err);
      setSnackbar({ open: true, message: 'Failed to load supplies transactions', severity: 'error' });
    } finally {
      setLoadingSuppliesTransactions(false);
    }
  };

  const handleOpenSuppliesTransactionDialog = () => {
    setSuppliesTransactionDialogOpen(true);
    fetchSuppliesTransactions();
  };

  const handleSelectSuppliesTransaction = async (transaction) => {
    try {
      let documentData = {
        invoice_file_path: '', invoice_file_name: '', invoice_file_type: '', invoice_file_size: null,
        po_file_path: '', po_file_name: '', po_file_type: '', po_file_size: null,
        coa_file_path: '', coa_file_name: '', coa_file_type: '', coa_file_size: null,
        msds_file_path: '', msds_file_name: '', msds_file_type: '', msds_file_size: null,
        bol_file_path: '', bol_file_name: '', bol_file_type: '', bol_file_size: null
      };

      setSnackbar({ open: true, message: 'Copying documents...', severity: 'info' });
      let copiedFiles = [];

      // Copy all document files
      if (transaction.invoice_file_path) {
        try {
          const result = await copyFileToBucket('supplies-transactions-invoices', transaction.invoice_file_path, transaction.invoice_file_name, transaction.invoice_file_type, transaction.invoice_file_size);
          if (result) { Object.assign(documentData, { invoice_file_path: result.file_path, invoice_file_name: result.file_name, invoice_file_type: result.file_type, invoice_file_size: result.file_size }); copiedFiles.push('Invoice'); }
        } catch (err) { console.error('Error copying invoice:', err); }
      }
      if (transaction.po_file_path) {
        try {
          const result = await copyFileToBucket('supplies-transactions-po', transaction.po_file_path, transaction.po_file_name, 'application/pdf', transaction.po_file_size);
          if (result) { Object.assign(documentData, { po_file_path: result.file_path, po_file_name: result.file_name, po_file_type: result.file_type, po_file_size: result.file_size }); copiedFiles.push('PO'); }
        } catch (err) { console.error('Error copying PO:', err); }
      }
      if (transaction.coa_file_path) {
        try {
          const result = await copyFileToBucket('supplies-transactions-coa', transaction.coa_file_path, transaction.coa_file_name, 'application/pdf', transaction.coa_file_size);
          if (result) { Object.assign(documentData, { coa_file_path: result.file_path, coa_file_name: result.file_name, coa_file_type: result.file_type, coa_file_size: result.file_size }); copiedFiles.push('COA'); }
        } catch (err) { console.error('Error copying COA:', err); }
      }
      if (transaction.msds_file_path) {
        try {
          const result = await copyFileToBucket('supplies-transactions-msds', transaction.msds_file_path, transaction.msds_file_name, 'application/pdf', transaction.msds_file_size);
          if (result) { Object.assign(documentData, { msds_file_path: result.file_path, msds_file_name: result.file_name, msds_file_type: result.file_type, msds_file_size: result.file_size }); copiedFiles.push('MSDS'); }
        } catch (err) { console.error('Error copying MSDS:', err); }
      }
      if (transaction.bol_file_path) {
        try {
          const result = await copyFileToBucket('supplies-transactions-bol', transaction.bol_file_path, transaction.bol_file_name, 'application/pdf', transaction.bol_file_size);
          if (result) { Object.assign(documentData, { bol_file_path: result.file_path, bol_file_name: result.file_name, bol_file_type: result.file_type, bol_file_size: result.file_size }); copiedFiles.push('BOL'); }
        } catch (err) { console.error('Error copying BOL:', err); }
      }

      setFormData(prev => ({
        ...prev,
        purchase_order_no: transaction.po_number || '',
        invoice_number: transaction.invoice_number || '',
        supplier_vendor_name: transaction.supplier?.name || '',
        material_name: transaction.supply?.name || '',
        quantity_ordered: Math.abs(transaction.quantity) || '',
        quantity_received: Math.abs(transaction.quantity) || '',
        location: transaction.location?.name || '',
        batch_no: transaction.batch_number || '',
        lot_no: transaction.lot_number || '',
        remarks: transaction.notes || '',
        ...documentData
      }));
      
      setSuppliesTransactionDialogOpen(false);
      const message = copiedFiles.length > 0
        ? `Form autofilled with ${copiedFiles.join(', ')} document${copiedFiles.length > 1 ? 's' : ''}`
        : 'Form autofilled from transaction';
      setSnackbar({ open: true, message, severity: 'success' });
    } catch (err) {
      console.error('Error autofilling from supplies transaction:', err);
      setSnackbar({ open: true, message: 'Error autofilling form', severity: 'error' });
    }
  };

  useEffect(() => {
    fetchCompanyData();
  }, [companyId]);

  // Handle category from URL
  useEffect(() => {
    if (category) {
      const cat = CATEGORIES.find(c => c.id === category);
      if (cat) {
        setSelectedCategory(cat);
      }
    } else {
      setSelectedCategory(null);
    }
    setSearchTerm('');
  }, [category]);

  useEffect(() => {
    if (selectedCategory) {
      fetchImports();
    }
  }, [selectedCategory]);

  const handleOpenDialog = async (importRecord = null) => {
    if (importRecord) {
      setEditMode(true);
      setFormData({
        id: importRecord.id,
        serial_number: importRecord.serial_number,
        date_of_receipt: dayjs(importRecord.date_of_receipt),
        purchase_order_no: importRecord.purchase_order_no || '',
        invoice_number: importRecord.invoice_number || '',
        supplier_vendor_name: importRecord.supplier_vendor_name || '',
        material_name: importRecord.material_name || '',
        material_description: importRecord.material_description || '',
        batch_no: importRecord.batch_no || '',
        lot_no: importRecord.lot_no || '',
        coa_received: importRecord.coa_received || false,
        manufacturing_date: importRecord.manufacturing_date ? dayjs(importRecord.manufacturing_date) : null,
        expiry_date: importRecord.expiry_date ? dayjs(importRecord.expiry_date) : null,
        container_type: importRecord.container_type || '',
        container_count: importRecord.container_count || '',
        unit_of_measure: importRecord.unit_of_measure || '',
        quantity_per_container: importRecord.quantity_per_container || '',
        quantity_ordered: importRecord.quantity_ordered || '',
        quantity_received: importRecord.quantity_received || '',
        location: importRecord.location || '',
        sampling_done: importRecord.sampling_done || false,
        label_status: importRecord.label_status || 'pending',
        qty_issued_to_production: importRecord.qty_issued_to_production || '',
        date_of_issue: importRecord.date_of_issue ? dayjs(importRecord.date_of_issue) : null,
        remarks: importRecord.remarks || '',
        invoice_file_path: importRecord.invoice_file_path || '',
        invoice_file_name: importRecord.invoice_file_name || '',
        invoice_file_type: importRecord.invoice_file_type || '',
        invoice_file_size: importRecord.invoice_file_size || null,
        po_file_path: importRecord.po_file_path || '',
        po_file_name: importRecord.po_file_name || '',
        po_file_type: importRecord.po_file_type || '',
        po_file_size: importRecord.po_file_size || null,
        coa_file_path: importRecord.coa_file_path || '',
        coa_file_name: importRecord.coa_file_name || '',
        coa_file_type: importRecord.coa_file_type || '',
        coa_file_size: importRecord.coa_file_size || null,
        msds_file_path: importRecord.msds_file_path || '',
        msds_file_name: importRecord.msds_file_name || '',
        msds_file_type: importRecord.msds_file_type || '',
        msds_file_size: importRecord.msds_file_size || null,
        bol_file_path: importRecord.bol_file_path || '',
        bol_file_name: importRecord.bol_file_name || '',
        bol_file_type: importRecord.bol_file_type || '',
        bol_file_size: importRecord.bol_file_size || null
      });
    } else {
      setEditMode(false);
      try {
        const { data, error } = await supabase
          .rpc('get_next_import_serial_number', {
            p_company_id: companyId,
            p_category: selectedCategory.id
          });
        if (!error && data) {
          setFormData({
            id: '',
            serial_number: data,
            date_of_receipt: dayjs(),
            purchase_order_no: '',
            invoice_number: '',
            supplier_vendor_name: '',
            material_name: '',
            material_description: '',
            batch_no: '',
            lot_no: '',
            coa_received: false,
            manufacturing_date: null,
            expiry_date: null,
            container_type: '',
            container_count: '',
            unit_of_measure: '',
            quantity_per_container: '',
            quantity_ordered: '',
            quantity_received: '',
            location: '',
            sampling_done: false,
            label_status: 'pending',
            qty_issued_to_production: '',
            date_of_issue: null,
            remarks: '',
            invoice_file_path: '',
            invoice_file_name: '',
            invoice_file_type: '',
            invoice_file_size: null,
            po_file_path: '',
            po_file_name: '',
            po_file_type: '',
            po_file_size: null,
            coa_file_path: '',
            coa_file_name: '',
            coa_file_type: '',
            coa_file_size: null,
            msds_file_path: '',
            msds_file_name: '',
            msds_file_type: '',
            msds_file_size: null,
            bol_file_path: '',
            bol_file_name: '',
            bol_file_type: '',
            bol_file_size: null
          });
        }
      } catch (err) {
        console.error('Error getting serial number:', err);
      }
    }
    setInvoiceFile(null);
    setPoFile(null);
    setCoaFile(null);
    setMsdsFile(null);
    setBolFile(null);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
  };

  const handleFileUpload = (event, fileType) => {
    const file = event.target.files?.[0];
    if (file) {
      switch (fileType) {
        case 'invoice':
      setInvoiceFile(file);
          break;
        case 'po':
          setPoFile(file);
          break;
        case 'coa':
          setCoaFile(file);
          break;
        case 'msds':
          setMsdsFile(file);
          break;
        case 'bol':
          setBolFile(file);
          break;
        default:
          break;
      }
    }
  };

  const uploadInvoiceFile = async (file) => {
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
      const filePath = `${companyId}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('import-documents')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      return {
        path: filePath,
        name: file.name,
        type: file.type,
        size: file.size
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    }
  };

  const removeDocumentFile = async (fileType) => {
    try {
      // Check delete permission before removing file
      if (!canDelete) {
        setSnackbar({ open: true, message: 'You do not have permission to delete files', severity: 'error' });
        return;
      }
      
      const filePathField = `${fileType}_file_path`;
      const filePath = formData[filePathField];
      
      if (filePath) {
        const { error } = await supabase.storage
          .from('import-documents')
          .remove([filePath]);

        if (error) throw error;
      }
      
      setFormData(prev => ({
        ...prev,
        [`${fileType}_file_path`]: '',
        [`${fileType}_file_name`]: '',
        [`${fileType}_file_type`]: '',
        [`${fileType}_file_size`]: null
      }));
      
      // Reset file state
      switch (fileType) {
        case 'invoice': setInvoiceFile(null); break;
        case 'po': setPoFile(null); break;
        case 'coa': setCoaFile(null); break;
        case 'msds': setMsdsFile(null); break;
        case 'bol': setBolFile(null); break;
      }
      
      setSnackbar({ open: true, message: `${fileType.toUpperCase()} file removed successfully`, severity: 'success' });
    } catch (error) {
      console.error('Error removing file:', error);
      setSnackbar({ open: true, message: 'Error removing file', severity: 'error' });
    }
  };

  const downloadInvoiceFile = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('import-documents')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setSnackbar({ open: true, message: 'Error downloading file', severity: 'error' });
    }
  };

  const viewInvoiceFile = async (filePath, fileName) => {
    try {
      const { data, error } = await supabase.storage
        .from('import-documents')
        .createSignedUrl(filePath, 3600); // 1 hour expiry

      if (error) throw error;

      window.open(data.signedUrl, '_blank');
    } catch (error) {
      console.error('Error viewing file:', error);
      setSnackbar({ open: true, message: 'Error viewing file', severity: 'error' });
    }
  };

  const handleSubmit = async () => {
    if (!selectedCategory) return;
    
    // Check edit permission
    if (!canEdit) {
      setError('You do not have permission to create or edit import records.');
      return;
    }
    
    setSubmitting(true);
    setError(null);

    try {
      // Upload all document files if new ones were selected
      let documentFileData = {
        invoice_file_path: formData.invoice_file_path,
        invoice_file_name: formData.invoice_file_name,
        invoice_file_type: formData.invoice_file_type,
        invoice_file_size: formData.invoice_file_size,
        po_file_path: formData.po_file_path,
        po_file_name: formData.po_file_name,
        po_file_type: formData.po_file_type,
        po_file_size: formData.po_file_size,
        coa_file_path: formData.coa_file_path,
        coa_file_name: formData.coa_file_name,
        coa_file_type: formData.coa_file_type,
        coa_file_size: formData.coa_file_size,
        msds_file_path: formData.msds_file_path,
        msds_file_name: formData.msds_file_name,
        msds_file_type: formData.msds_file_type,
        msds_file_size: formData.msds_file_size,
        bol_file_path: formData.bol_file_path,
        bol_file_name: formData.bol_file_name,
        bol_file_type: formData.bol_file_type,
        bol_file_size: formData.bol_file_size
      };

        setUploadingInvoice(true);
      
      if (invoiceFile) {
        const uploadedFile = await uploadInvoiceFile(invoiceFile);
        documentFileData.invoice_file_path = uploadedFile.path;
        documentFileData.invoice_file_name = uploadedFile.name;
        documentFileData.invoice_file_type = uploadedFile.type;
        documentFileData.invoice_file_size = uploadedFile.size;
      }
      
      if (poFile) {
        const uploadedFile = await uploadInvoiceFile(poFile);
        documentFileData.po_file_path = uploadedFile.path;
        documentFileData.po_file_name = uploadedFile.name;
        documentFileData.po_file_type = uploadedFile.type;
        documentFileData.po_file_size = uploadedFile.size;
      }
      
      if (coaFile) {
        const uploadedFile = await uploadInvoiceFile(coaFile);
        documentFileData.coa_file_path = uploadedFile.path;
        documentFileData.coa_file_name = uploadedFile.name;
        documentFileData.coa_file_type = uploadedFile.type;
        documentFileData.coa_file_size = uploadedFile.size;
      }
      
      if (msdsFile) {
        const uploadedFile = await uploadInvoiceFile(msdsFile);
        documentFileData.msds_file_path = uploadedFile.path;
        documentFileData.msds_file_name = uploadedFile.name;
        documentFileData.msds_file_type = uploadedFile.type;
        documentFileData.msds_file_size = uploadedFile.size;
      }
      
      if (bolFile) {
        const uploadedFile = await uploadInvoiceFile(bolFile);
        documentFileData.bol_file_path = uploadedFile.path;
        documentFileData.bol_file_name = uploadedFile.name;
        documentFileData.bol_file_type = uploadedFile.type;
        documentFileData.bol_file_size = uploadedFile.size;
      }
      
      setUploadingInvoice(false);

      const importData = {
        company_id: companyId,
        category: selectedCategory.id,
        serial_number: parseInt(formData.serial_number),
        date_of_receipt: formData.date_of_receipt ? formData.date_of_receipt.format('YYYY-MM-DD') : null,
        purchase_order_no: formData.purchase_order_no.trim() || null,
        invoice_number: formData.invoice_number.trim() || null,
        supplier_vendor_name: formData.supplier_vendor_name.trim() || null,
        material_name: formData.material_name.trim() || null,
        material_description: formData.material_description.trim() || null,
        batch_no: formData.batch_no.trim() || null,
        lot_no: formData.lot_no.trim() || null,
        coa_received: formData.coa_received,
        manufacturing_date: formData.manufacturing_date ? formData.manufacturing_date.format('YYYY-MM-DD') : null,
        expiry_date: formData.expiry_date ? formData.expiry_date.format('YYYY-MM-DD') : null,
        container_type: formData.container_type.trim() || null,
        container_count: formData.container_count ? parseInt(formData.container_count) : null,
        unit_of_measure: formData.unit_of_measure.trim() || 'units',
        quantity_per_container: formData.quantity_per_container ? parseFloat(formData.quantity_per_container) : null,
        quantity_ordered: formData.quantity_ordered ? parseFloat(formData.quantity_ordered) : null,
        quantity_received: formData.quantity_received ? parseFloat(formData.quantity_received) : 0,
        location: formData.location.trim() || null,
        sampling_done: formData.sampling_done,
        label_status: formData.label_status || 'pending',
        qty_issued_to_production: formData.qty_issued_to_production ? parseFloat(formData.qty_issued_to_production) : 0,
        date_of_issue: formData.date_of_issue ? formData.date_of_issue.format('YYYY-MM-DD') : null,
        remarks: formData.remarks.trim() || null,
        ...documentFileData
      };

      if (editMode) {
        const { error } = await supabase
          .from('imports')
          .update({ ...importData, updated_at: new Date().toISOString() })
          .eq('id', formData.id);
        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('imports')
          .insert(importData);
        if (error) throw error;
      }

      await fetchImports();
      handleCloseDialog();
      setSnackbar({
        open: true,
        message: `Import record ${editMode ? 'updated' : 'added'} successfully`,
        severity: 'success'
      });
    } catch (err) {
      console.error('Error saving import:', err);
      setError(`Failed to ${editMode ? 'update' : 'create'} import record.`);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteClick = (importRecord) => {
    setImportToDelete(importRecord);
    setDeleteConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!importToDelete) return;
    
    // Check delete permission
    if (!canDelete) {
      setSnackbar({ open: true, message: 'You do not have permission to delete import records', severity: 'error' });
      setDeleteConfirmOpen(false);
      setImportToDelete(null);
      return;
    }
    
    try {
      // Delete all document files from storage bucket if they exist
      const filesToDelete = [];
      if (importToDelete.invoice_file_path) filesToDelete.push(importToDelete.invoice_file_path);
      if (importToDelete.po_file_path) filesToDelete.push(importToDelete.po_file_path);
      if (importToDelete.coa_file_path) filesToDelete.push(importToDelete.coa_file_path);
      if (importToDelete.msds_file_path) filesToDelete.push(importToDelete.msds_file_path);
      if (importToDelete.bol_file_path) filesToDelete.push(importToDelete.bol_file_path);
      
      if (filesToDelete.length > 0) {
        try {
          const { error: storageError } = await supabase.storage
            .from('import-documents')
            .remove(filesToDelete);
          
          if (storageError) {
            console.error('Error deleting files from storage:', storageError);
            // Continue with deletion even if storage deletion fails
          }
        } catch (storageErr) {
          console.error('Error deleting files:', storageErr);
          // Continue with deletion even if storage deletion fails
        }
      }

      // Delete the import record
      const { error, data } = await supabase
        .from('imports')
        .delete()
        .eq('id', importToDelete.id)
        .eq('company_id', companyId);
      
      if (error) {
        console.error('Delete error:', error);
        throw error;
      }
      
      await fetchImports();
      setSnackbar({ open: true, message: 'Import record deleted successfully', severity: 'success' });
    } catch (err) {
      console.error('Error in handleConfirmDelete:', err);
      setSnackbar({ open: true, message: `Error deleting record: ${err.message}`, severity: 'error' });
    }
    setDeleteConfirmOpen(false);
    setImportToDelete(null);
  };

  const handleReviewImport = (importRecord) => {
    setApprovalImport(importRecord);
    setSignatureName('');
    setApprovalNotes('');
    setApprovalDialogOpen(true);
  };

  const handleApprovalSubmit = async () => {
    try {
      // Check approve permission
      if (!canApprove) {
        setSnackbar({ open: true, message: 'You do not have permission to approve import records', severity: 'error' });
        return;
      }
      
      if (!signatureName.trim()) {
        setSnackbar({ open: true, message: 'Signature name is required for approval', severity: 'error' });
        return;
      }

      const approvalData = {
        approved_by_signature: signatureName,
        approved_at: new Date().toISOString(),
        approval_notes: approvalNotes || null,
        status: 'approved'
      };

      const { error } = await supabase
        .from('imports')
        .update(approvalData)
        .eq('id', approvalImport.id);

      if (error) throw error;

      setApprovalDialogOpen(false);
      setSignatureName('');
      setApprovalNotes('');
      setApprovalImport(null);
      await fetchImports();
      setSnackbar({ open: true, message: 'Import record approved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error approving import:', err);
      setSnackbar({ open: true, message: `Error approving record: ${err.message}`, severity: 'error' });
    }
  };

  const handleUnapproveImport = async (importRecord) => {
    try {
      // Check approve permission (same permission needed to unapprove)
      if (!canApprove) {
        setSnackbar({ open: true, message: 'You do not have permission to unapprove import records', severity: 'error' });
        return;
      }
      
      const unapprovalData = {
        approved_by_signature: null,
        approved_at: null,
        approval_notes: null,
        status: 'pending'
      };

      const { error } = await supabase
        .from('imports')
        .update(unapprovalData)
        .eq('id', importRecord.id);

      if (error) throw error;

      await fetchImports();
      setSnackbar({ open: true, message: 'Import record unapproved successfully', severity: 'success' });
    } catch (err) {
      console.error('Error unapproving import:', err);
      setSnackbar({ open: true, message: `Error unapproving record: ${err.message}`, severity: 'error' });
    }
  };

  const handleViewImport = (importRecord) => {
    setViewImport(importRecord);
    setViewDialogOpen(true);
  };

  const formatDate = (date) => {
    if (!date) return '—';
    return dayjs(date).format('MMM DD, YYYY');
  };

  const formatNumber = (value, category = null) => {
    if (!value && value !== 0) return '—';
    // For caps, bottles, labels, supplies - show as integer (no decimals)
    if (category && ['caps', 'bottles', 'labels', 'supplies'].includes(category)) {
      return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value);
    }
    // For raw_materials - show with decimals
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 3 }).format(value);
  };

  // Filter categories and imports based on search term
  useEffect(() => {
    if (!selectedCategory) {
      // Viewing categories
      if (!searchTerm.trim()) {
        setFilteredCategories(CATEGORIES);
        return;
      }
      const searchLower = searchTerm.toLowerCase();
      const filtered = CATEGORIES.filter(cat => 
        cat.name?.toLowerCase().includes(searchLower)
      );
      setFilteredCategories(filtered);
      setPage(0);
    } else {
      // Viewing imports
      if (!searchTerm.trim()) {
        setFilteredImports(imports);
        return;
      }
      const searchLower = searchTerm.toLowerCase();
      const filtered = imports.filter(imp => 
        imp.material_name?.toLowerCase().includes(searchLower) ||
        imp.supplier_vendor_name?.toLowerCase().includes(searchLower) ||
        imp.batch_no?.toLowerCase().includes(searchLower) ||
        imp.lot_no?.toLowerCase().includes(searchLower) ||
        imp.purchase_order_no?.toLowerCase().includes(searchLower) ||
        imp.invoice_number?.toLowerCase().includes(searchLower)
      );
      setFilteredImports(filtered);
      setPage(0);
    }
  }, [searchTerm, imports, selectedCategory]);

  // Filter transactions based on search term
  useEffect(() => {
    if (!transactionSearchTerm.trim()) {
      setFilteredTransactions(transactions);
      return;
    }
    const searchLower = transactionSearchTerm.toLowerCase();
    const filtered = transactions.filter(txn => 
      txn.raw_material?.name?.toLowerCase().includes(searchLower) ||
      txn.supplier?.name?.toLowerCase().includes(searchLower) ||
      txn.invoice_number?.toLowerCase().includes(searchLower) ||
      txn.po_number?.toLowerCase().includes(searchLower) ||
      txn.batch_number?.toLowerCase().includes(searchLower) ||
      txn.lot_number?.toLowerCase().includes(searchLower) ||
      txn.reference_number?.toLowerCase().includes(searchLower)
    );
    setFilteredTransactions(filtered);
  }, [transactionSearchTerm, transactions]);

  // Filter caps transactions based on search term
  useEffect(() => {
    if (!capsTransactionSearchTerm.trim()) {
      setFilteredCapsTransactions(capsTransactions);
      return;
    }
    const searchLower = capsTransactionSearchTerm.toLowerCase();
    const filtered = capsTransactions.filter(txn => 
      txn.cap?.name?.toLowerCase().includes(searchLower) ||
      txn.supplier?.name?.toLowerCase().includes(searchLower) ||
      txn.invoice_number?.toLowerCase().includes(searchLower) ||
      txn.po_number?.toLowerCase().includes(searchLower) ||
      txn.batch_number?.toLowerCase().includes(searchLower) ||
      txn.lot_number?.toLowerCase().includes(searchLower) ||
      txn.reference_number?.toLowerCase().includes(searchLower)
    );
    setFilteredCapsTransactions(filtered);
  }, [capsTransactionSearchTerm, capsTransactions]);

  // Filter bottles transactions based on search term
  useEffect(() => {
    if (!bottlesTransactionSearchTerm.trim()) {
      setFilteredBottlesTransactions(bottlesTransactions);
      return;
    }
    const searchLower = bottlesTransactionSearchTerm.toLowerCase();
    const filtered = bottlesTransactions.filter(txn => 
      txn.bottle?.name?.toLowerCase().includes(searchLower) ||
      txn.supplier?.name?.toLowerCase().includes(searchLower) ||
      txn.invoice_number?.toLowerCase().includes(searchLower) ||
      txn.po_number?.toLowerCase().includes(searchLower) ||
      txn.batch_number?.toLowerCase().includes(searchLower) ||
      txn.lot_number?.toLowerCase().includes(searchLower) ||
      txn.reference_number?.toLowerCase().includes(searchLower)
    );
    setFilteredBottlesTransactions(filtered);
  }, [bottlesTransactionSearchTerm, bottlesTransactions]);

  // Filter labels transactions based on search term
  useEffect(() => {
    if (!labelsTransactionSearchTerm.trim()) {
      setFilteredLabelsTransactions(labelsTransactions);
      return;
    }
    const searchLower = labelsTransactionSearchTerm.toLowerCase();
    const filtered = labelsTransactions.filter(txn => 
      txn.label?.name?.toLowerCase().includes(searchLower) ||
      txn.supplier?.name?.toLowerCase().includes(searchLower) ||
      txn.invoice_number?.toLowerCase().includes(searchLower) ||
      txn.po_number?.toLowerCase().includes(searchLower) ||
      txn.batch_number?.toLowerCase().includes(searchLower) ||
      txn.lot_number?.toLowerCase().includes(searchLower) ||
      txn.reference_number?.toLowerCase().includes(searchLower)
    );
    setFilteredLabelsTransactions(filtered);
  }, [labelsTransactionSearchTerm, labelsTransactions]);

  // Filter supplies transactions based on search term
  useEffect(() => {
    if (!suppliesTransactionSearchTerm.trim()) {
      setFilteredSuppliesTransactions(suppliesTransactions);
      return;
    }
    const searchLower = suppliesTransactionSearchTerm.toLowerCase();
    const filtered = suppliesTransactions.filter(txn => 
      txn.supply?.name?.toLowerCase().includes(searchLower) ||
      txn.supplier?.name?.toLowerCase().includes(searchLower) ||
      txn.invoice_number?.toLowerCase().includes(searchLower) ||
      txn.po_number?.toLowerCase().includes(searchLower) ||
      txn.batch_number?.toLowerCase().includes(searchLower) ||
      txn.lot_number?.toLowerCase().includes(searchLower) ||
      txn.reference_number?.toLowerCase().includes(searchLower)
    );
    setFilteredSuppliesTransactions(filtered);
  }, [suppliesTransactionSearchTerm, suppliesTransactions]);

  // Permission check - must be after all hooks
  if (!hasPermission('inventory.imports.view', 'read')) {
    return (
      <Container maxWidth="lg" sx={{ py: 8 }}>
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          justifyContent: 'center',
          minHeight: '70vh',
          textAlign: 'center'
        }}>
          <Card sx={{
            p: 8,
            borderRadius: 3,
            border: `1px solid ${theme.palette.grey[400]}`,
            background: 'white',
            boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
            maxWidth: 600,
            width: '100%',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'default',
            transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
            '&:hover': {
              transform: 'translateY(-8px)',
              boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
            },
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: 4,
              background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
              opacity: 0.8,
              transition: 'opacity 0.3s ease'
            },
            '&:hover::before': {
              opacity: 1
            }
          }}>
            <Box sx={{ 
              p: 3, 
              borderRadius: 3, 
              background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
              border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
              mb: 6,
              display: 'inline-flex',
              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
            }}>
              <InputIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
            </Box>
            <Typography variant="h3" sx={{ 
              mb: 3, 
              fontWeight: 500, 
              color: 'text.primary',
              fontSize: '2.5rem',
              letterSpacing: '-0.8px'
            }}>
              Access Restricted
            </Typography>
            <Typography variant="h6" sx={{ 
              color: 'text.secondary', 
              mb: 4,
              fontWeight: 500,
              fontSize: '1.2rem'
            }}>
              Imports Log Permissions Required
            </Typography>
            <Typography variant="body1" sx={{ 
              color: 'text.secondary',
              lineHeight: 1.7,
              mb: 6,
              fontSize: '1.05rem',
              maxWidth: 450,
              mx: 'auto'
            }}>
              You don't currently have the necessary permissions to access the Imports Log. 
              Contact your system administrator to request access or verify your role permissions.
            </Typography>
            <Button
              variant="outlined"
              onClick={() => navigate(-1)}
              sx={{
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                px: 4,
                py: 1.5,
                fontSize: '1rem'
              }}
            >
              Return to Previous Page
            </Button>
          </Card>
        </Box>
      </Container>
    );
  }
  
  // Check edit, delete, and approve permissions
  const canEdit = hasPermission('inventory.imports.edit', 'write');
  const canDelete = hasPermission('inventory.imports.delete', 'delete');
  const canApprove = hasPermission('inventory.imports.approve', 'write');

  if (loading && !selectedCategory) {
    return (
      <Container disableGutters maxWidth="xl" sx={{ px: 3, py: 4, pb: 16 }}>
        <Card sx={{ 
          borderRadius: 3,
          mt: 10,
          mx: 'auto',
          maxWidth: 600,
          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
          background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
          boxShadow: `0 12px 32px ${alpha('#009688', 0.15)}`,
        }}>
          <CardContent sx={{ p: 6, textAlign: 'center' }}>
            <Box sx={{ mb: 3 }}>
              <Box sx={{
                display: 'inline-flex',
                p: 3,
                borderRadius: '50%',
                bgcolor: alpha('#009688', 0.1),
                mb: 2
              }}>
                <InputIcon sx={{ fontSize: 48, color: '#009688' }} />
              </Box>
            </Box>
            <Typography variant="h6" sx={{
              fontWeight: 500,
              color: '#009688',
              mb: 1,
              fontSize: '1.1rem'
            }}>
              Loading Imports
            </Typography>
            <Typography variant="body2" sx={{
              color: 'text.secondary',
              fontWeight: 500,
              maxWidth: 400,
              mx: 'auto',
              lineHeight: 1.6,
              mb: 3
            }}>
              Analyzing import data...
            </Typography>
            <Box sx={{
              width: '60%',
              mx: 'auto',
              '& .MuiLinearProgress-root': {
                height: 8,
                borderRadius: 4,
                bgcolor: alpha(theme.palette.grey[300], 0.3),
                '& .MuiLinearProgress-bar': {
                  bgcolor: '#009688',
                  borderRadius: 4
                }
              }
            }}>
              <LinearProgress />
            </Box>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Fade in timeout={800}>
        <Box>
          <Container maxWidth="xl" sx={{ py: 3, pb: 16 }}>
            {/* Header Section */}
            <Box sx={{ mb: 3 }}>
              <Card sx={{ 
                borderRadius: 3,
                mt: 5,
                mb: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: theme.palette.grey[100],
                backdropFilter: 'blur(20px)',
                borderTop: '3px solid #009688',
                boxShadow: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                    {selectedCategory && (
                      <Tooltip title="Back to Categories">
                        <IconButton
                          onClick={() => {
                            setSearchTerm('');
                            setPage(0);
                            navigate(`/company/${companyId}/inventory/imports`);
                          }}
                          sx={{
                            bgcolor: 'white',
                            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                            borderRadius: 2,
                            transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                            '&:hover': {
                              bgcolor: 'primary.main',
                              color: 'white',
                              transform: 'scale(1.05)',
                              boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`
                            }
                          }}
                        >
                          <ArrowBackIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box sx={{
                      p: 2,
                      borderRadius: 2,
                      bgcolor: alpha('#009688', 0.08),
                    }}>
                      <InputIcon sx={{ fontSize: 32, color: '#009688' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h4" sx={{
                        fontWeight: 500,
                        color: '#009688',
                        mb: 1,
                        fontSize: '2rem',
                        letterSpacing: '-0.5px'
                      }}>
                        {selectedCategory ? selectedCategory.name : 'Imports Log'}
                      </Typography>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.secondary', 
                        fontWeight: 500,
                        fontSize: '1rem'
                      }}>
                        {companyName}
                      </Typography>
                    </Box>
                  </Box>
                  <Typography variant="body2" sx={{ 
                    color: 'text.secondary', 
                    maxWidth: 700, 
                    lineHeight: 1.6,
                    fontSize: '0.95rem',
                    fontWeight: 500
                  }}>
                    {selectedCategory 
                      ? 'Track and manage import records, including receipts, quality control, and inventory status.'
                      : 'Select a category to view and manage import records.'}
                  </Typography>
                </CardContent>
              </Card>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>{error}</Alert>}

            {/* Search Bar - Only show when category is selected */}
            {selectedCategory && (
              <Card sx={{
                height: 100,
                mb: 2,
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                borderRadius: 3,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                boxShadow: `0 8px 24px ${alpha(theme.palette.grey[500], 0.12)}`,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <CardContent>
                  <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField
                      placeholder="Search imports..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      size="small"
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                          </InputAdornment>
                        ),
                      }}
                      sx={{
                        minWidth: 280,
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 3,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          background: 'white',
                          height: 40,
                          '& fieldset': { border: 'none' },
                          '&:hover': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                          },
                          '&.Mui-focused': {
                            border: `1px solid ${theme.palette.primary.main}`,
                            boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                          }
                        },
                      '& .MuiInputBase-input': {
                        fontSize: '0.9rem',
                        fontWeight: 500,
                        py: 1.25
                      }
                    }}
                    />
                    
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {`${filteredImports.length} import record${filteredImports.length !== 1 ? 's' : ''}`}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Categories View */}
            {!selectedCategory && (
              <Paper sx={{ 
                borderRadius: 3, 
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                }
              }}>
                <TableContainer sx={{ overflow: 'visible' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell 
                          sx={{ 
                            fontWeight: 500,
                            fontSize: '0.9rem',
                            bgcolor: 'white',
                            borderBottom: `2px solid ${theme.palette.primary.main}`,
                            color: 'primary.main',
                            py: 2,
                            px: 3
                          }}
                        >
                          Category Name
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredCategories.map((cat) => (
                        <TableRow 
                          key={cat.id} 
                          hover
                          sx={{ 
                            cursor: 'pointer',
                            transition: 'all 0.2s ease',
                            '&:hover': { 
                              bgcolor: 'grey.50',
                              transform: 'translateX(4px)',
                              transition: 'all 0.2s ease',
                              boxShadow: 'inset 4px 0 0 black'
                            },
                            '& .MuiTableCell-root': {
                              borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                              py: 3,
                              px: 3
                            }
                          }}
                          onClick={() => navigate(`/company/${companyId}/inventory/imports/${cat.id}`)}
                        >
                          <TableCell>
                            <Typography 
                              variant="h6" 
                              sx={{ 
                                fontWeight: 400,
                                color: 'text.primary',
                                fontSize: '1.05rem'
                              }}
                            >
                              {cat.name}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            )}

            {/* Imports Table View */}
            {selectedCategory && (
              <>
                <Paper sx={{ 
                  borderRadius: 3, 
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.grey[100], 0.8)}, ${alpha(theme.palette.grey[50], 0.9)})`,
                  boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                  overflow: 'hidden',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 20px 40px ${alpha(theme.palette.primary.main, 0.25)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                  }
                }}>
                  <TableContainer sx={{ 
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                      display: 'none'
                    },
                    msOverflowStyle: 'none',
                    scrollbarWidth: 'none'
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>S.No</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Location</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Date of Receipt</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Supplier/Vendor</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Material Name</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Batch No.</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Lot No.</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Qty Received</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Balance</TableCell>
                          <TableCell sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2 }}>Status</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, fontSize: '0.85rem', bgcolor: 'white', borderBottom: `2px solid ${theme.palette.primary.main}`, color: 'primary.main', py: 2, px: 2, width: 180 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredImports.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((imp, index) => (
                          <TableRow 
                            key={`import-row-${imp.id}-${index}`}
                            hover
                            onClick={() => handleViewImport(imp)}
                            sx={{ 
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              bgcolor: 'white',
                              '&:hover': { 
                                bgcolor: 'grey.50',
                                transform: 'translateX(4px)',
                                transition: 'all 0.2s ease',
                                boxShadow: 'inset 4px 0 0 black'
                              },
                              '& .MuiTableCell-root': {
                                borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                                py: 2,
                                px: 2,
                                fontSize: '0.85rem'
                              }
                            }}
                          >
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                {imp.serial_number}
                              </Typography>
                            </TableCell>
                            <TableCell>{imp.location || '—'}</TableCell>
                            <TableCell>{formatDate(imp.date_of_receipt)}</TableCell>
                            <TableCell>{imp.supplier_vendor_name || '—'}</TableCell>
                            <TableCell>{imp.material_name || '—'}</TableCell>
                            <TableCell>{imp.batch_no || '—'}</TableCell>
                            <TableCell>{imp.lot_no || '—'}</TableCell>
                            <TableCell>
                              {imp.container_type && imp.container_count ? (
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {imp.container_count} {imp.container_type}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    ({formatNumber(imp.quantity_received, selectedCategory?.id)} {imp.unit_of_measure})
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="body2">
                                  {formatNumber(imp.quantity_received, selectedCategory?.id)} {imp.unit_of_measure}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontWeight: 500, color: 'primary.main' }}>
                                {formatNumber(imp.balance_quantity, selectedCategory?.id)} {imp.unit_of_measure}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {imp.status === 'approved' ? (
                                <Chip label="Approved" color="success" size="small" icon={<VerifiedIcon />} />
                              ) : (
                                <Chip label="Pending" color="default" size="small" />
                              )}
                            </TableCell>
                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                                <Tooltip title={canEdit ? "Edit" : "No permission to edit"}>
                                  <span>
                                    <IconButton 
                                      size="small"
                                      onClick={() => handleOpenDialog(imp)}
                                      disabled={!canEdit}
                                      sx={{
                                        color: canEdit ? 'primary.main' : 'grey.400',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                        borderRadius: 1,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                        '&:hover:not(:disabled)': {
                                          bgcolor: 'primary.main',
                                          color: 'white',
                                          transform: 'scale(1.05)',
                                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.3)}`
                                        },
                                        '&.Mui-disabled': {
                                          color: 'grey.300',
                                          borderColor: alpha(theme.palette.grey[300], 0.3)
                                        }
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                <Tooltip title={canDelete ? "Delete" : "No permission to delete"}>
                                  <span>
                                    <IconButton 
                                      size="small"
                                      onClick={() => handleDeleteClick(imp)}
                                      disabled={!canDelete}
                                      sx={{
                                        color: canDelete ? 'error.main' : 'grey.400',
                                        border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                        borderRadius: 1,
                                        transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                        '&:hover:not(:disabled)': {
                                          bgcolor: 'error.main',
                                          color: 'white',
                                          transform: 'scale(1.05)',
                                          boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.3)}`
                                        },
                                        '&.Mui-disabled': {
                                          color: 'grey.300',
                                          borderColor: alpha(theme.palette.grey[300], 0.3)
                                        }
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </span>
                                </Tooltip>
                                {imp.status !== 'approved' ? (
                                  <Tooltip title={canApprove ? "Approve" : "No permission to approve"}>
                                    <span>
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleReviewImport(imp)}
                                        disabled={!canApprove}
                                        sx={{
                                          color: canApprove ? 'success.main' : 'grey.400',
                                          border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                          borderRadius: 1,
                                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                          '&:hover:not(:disabled)': {
                                            bgcolor: 'success.main',
                                            color: 'white',
                                            transform: 'scale(1.05)',
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`
                                          },
                                          '&.Mui-disabled': {
                                            color: 'grey.300',
                                            borderColor: alpha(theme.palette.grey[300], 0.3)
                                          }
                                        }}
                                      >
                                        <CheckIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                ) : (
                                  <Tooltip title={canApprove ? "Unapprove" : "No permission to unapprove"}>
                                    <span>
                                      <IconButton 
                                        size="small"
                                        onClick={() => handleUnapproveImport(imp)}
                                        disabled={!canApprove}
                                        sx={{
                                          color: canApprove ? 'warning.main' : 'grey.400',
                                          border: `1px solid ${alpha(theme.palette.grey[400], 0.3)}`,
                                          borderRadius: 1,
                                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                                          '&:hover:not(:disabled)': {
                                            bgcolor: 'warning.main',
                                            color: 'white',
                                            transform: 'scale(1.05)',
                                            boxShadow: `0 2px 8px ${alpha(theme.palette.warning.main, 0.3)}`
                                          },
                                          '&.Mui-disabled': {
                                            color: 'grey.300',
                                            borderColor: alpha(theme.palette.grey[300], 0.3)
                                          }
                                        }}
                                      >
                                        <CancelIcon fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                        {filteredImports.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={11} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                No import records yet. Click the + button to add the first record.
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'center', 
                    mt: 3,
                    mb: 4,
                    px: 3
                  }}>
                    <Paper sx={{
                      borderRadius: 3,
                      border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                      background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                      boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                      overflow: 'hidden'
                    }}>
                      <TablePagination
                        component="div"
                        count={filteredImports.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                          setRowsPerPage(parseInt(e.target.value, 10));
                          setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50]}
                      />
                    </Paper>
                  </Box>
                </Paper>

                {canEdit && (
                  <Tooltip title="Add Import Record">
                    <Fab
                      color="primary"
                      onClick={() => handleOpenDialog()}
                      sx={{ 
                        position: 'fixed', 
                        bottom: 24, 
                        right: 24, 
                        width: 100, 
                        height: 100
                      }}
                    >
                      <AddIcon sx={{ fontSize: 32 }} />
                    </Fab>
                  </Tooltip>
                )}
              </>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
              <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
                <Box>{editMode ? 'Edit Import Record' : 'Add Import Record'}</Box>
                {!editMode && (
                  <>
                    {selectedCategory?.id === 'raw_materials' && (
                      <Button
                        variant="outlined"
                        startIcon={<InputIcon />}
                        onClick={handleOpenTransactionDialog}
                        size="small"
                      >
                        Autofill with Transaction
                      </Button>
                    )}
                    {selectedCategory?.id === 'caps' && (
                      <Button
                        variant="outlined"
                        startIcon={<InputIcon />}
                        onClick={handleOpenCapsTransactionDialog}
                        size="small"
                      >
                        Autofill with Transaction
                      </Button>
                    )}
                    {selectedCategory?.id === 'bottles' && (
                      <Button
                        variant="outlined"
                        startIcon={<InputIcon />}
                        onClick={handleOpenBottlesTransactionDialog}
                        size="small"
                      >
                        Autofill with Transaction
                      </Button>
                    )}
                    {selectedCategory?.id === 'labels' && (
                      <Button
                        variant="outlined"
                        startIcon={<InputIcon />}
                        onClick={handleOpenLabelsTransactionDialog}
                        size="small"
                      >
                        Autofill with Transaction
                      </Button>
                    )}
                    {selectedCategory?.id === 'supplies' && (
                      <Button
                        variant="outlined"
                        startIcon={<InputIcon />}
                        onClick={handleOpenSuppliesTransactionDialog}
                        size="small"
                      >
                        Autofill with Transaction
                      </Button>
                    )}
                  </>
                )}
              </DialogTitle>
              <DialogContent>
                <Box sx={{ pt: 2 }}>
                  <Grid container spacing={3}>
                    {/* General Information Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        fontSize: '1.1rem'
                      }}>
                        General Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="S.No"
                        value={formData.serial_number}
                        disabled
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <DatePicker
                        label="Date of Receipt"
                        value={formData.date_of_receipt}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, date_of_receipt: newValue }))}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Purchase Order No."
                        value={formData.purchase_order_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, purchase_order_no: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Invoice Number"
                        value={formData.invoice_number}
                        onChange={(e) => setFormData(prev => ({ ...prev, invoice_number: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <TextField
                        label="Supplier/Vendor Name"
                        value={formData.supplier_vendor_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, supplier_vendor_name: e.target.value }))}
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>

                    {/* Material Details Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Material Details
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Material Name"
                        value={formData.material_name}
                        onChange={(e) => setFormData(prev => ({ ...prev, material_name: e.target.value }))}
                        fullWidth
                        size="small"
                        required
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Material Description"
                        value={formData.material_description}
                        onChange={(e) => setFormData(prev => ({ ...prev, material_description: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Batch No."
                        value={formData.batch_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, batch_no: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Lot No."
                        value={formData.lot_no}
                        onChange={(e) => setFormData(prev => ({ ...prev, lot_no: e.target.value }))}
                        fullWidth
                        size="small"
                      />
                    </Grid>

                    {/* Dates & Expiry Section - Only for raw_materials and products */}
                    {selectedCategory && (selectedCategory.id === 'raw_materials' || selectedCategory.id === 'products') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 1,
                            mt: 2,
                            fontSize: '1.1rem'
                          }}>
                            Dates & Expiry
                          </Typography>
                          <Divider sx={{ mb: 2 }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label="Manufacturing Date"
                            value={formData.manufacturing_date}
                            onChange={(newValue) => setFormData(prev => ({ ...prev, manufacturing_date: newValue }))}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <DatePicker
                            label="Expiry Date"
                            value={formData.expiry_date}
                            onChange={(newValue) => setFormData(prev => ({ ...prev, expiry_date: newValue }))}
                            slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                          />
                        </Grid>
                      </>
                    )}

                    {/* Quantity Information Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Quantity Information
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    
                    {/* Show detailed fields only for raw_materials and products */}
                    {selectedCategory && (selectedCategory.id === 'raw_materials' || selectedCategory.id === 'products') && (
                      <>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Container Type"
                            value={formData.container_type}
                            onChange={(e) => setFormData(prev => ({ ...prev, container_type: e.target.value }))}
                            fullWidth
                            size="small"
                            placeholder="e.g., Drums, Bottles, Bags, Pallets"
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Number of Containers"
                            value={formData.container_count}
                            onChange={(e) => setFormData(prev => ({ ...prev, container_count: e.target.value }))}
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="e.g., 8"
                          />
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Unit of Measure"
                            value={formData.unit_of_measure}
                            onChange={(e) => setFormData(prev => ({ ...prev, unit_of_measure: e.target.value }))}
                            fullWidth
                            size="small"
                            placeholder="kg, L, units, etc."
                            required
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label="Total Quantity"
                            value={formData.quantity_per_container}
                            onChange={(e) => setFormData(prev => ({ ...prev, quantity_per_container: e.target.value }))}
                            fullWidth
                            size="small"
                            type="number"
                            placeholder="e.g., 1800"
                          />
                        </Grid>
                      </>
                    )}
                    
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Total Quantity Ordered"
                        value={formData.quantity_ordered}
                        onChange={(e) => setFormData(prev => ({ ...prev, quantity_ordered: e.target.value }))}
                        fullWidth
                        size="small"
                        type="number"
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Total Quantity Received"
                        value={formData.quantity_received}
                        onChange={(e) => setFormData(prev => ({ ...prev, quantity_received: e.target.value }))}
                        fullWidth
                        size="small"
                        type="number"
                        required
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Storage Location"
                        value={formData.location}
                        onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
                        fullWidth
                        size="small"
                        placeholder="e.g., Warehouse A, Shelf 3, etc."
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <TextField
                        label="Qty Issued to Production"
                        value={formData.qty_issued_to_production}
                        onChange={(e) => setFormData(prev => ({ ...prev, qty_issued_to_production: e.target.value }))}
                        fullWidth
                        size="small"
                        type="number"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip title="Use same as received quantity">
                                <IconButton
                                  size="small"
                                  onClick={() => setFormData(prev => ({ ...prev, qty_issued_to_production: prev.quantity_received }))}
                                  edge="end"
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': {
                                      bgcolor: alpha(theme.palette.primary.main, 0.1)
                                    }
                                  }}
                                >
                                  <CheckIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          )
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <DatePicker
                        label="Date of Issue"
                        value={formData.date_of_issue}
                        onChange={(newValue) => setFormData(prev => ({ ...prev, date_of_issue: newValue }))}
                        slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                      />
                    </Grid>

                    {/* Quality Control Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Quality Control
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <FormControl fullWidth size="small">
                        <InputLabel>Label Status</InputLabel>
                        <Select
                          value={formData.label_status}
                          label="Label Status"
                          onChange={(e) => setFormData(prev => ({ ...prev, label_status: e.target.value }))}
                        >
                          <MenuItem value="pending">Pending</MenuItem>
                          <MenuItem value="approved">Approved</MenuItem>
                          <MenuItem value="rejected">Rejected</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    {selectedCategory && (selectedCategory.id === 'raw_materials' || selectedCategory.id === 'products') && (
                      <Grid item xs={12} md={4}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={formData.coa_received}
                              onChange={(e) => setFormData(prev => ({ ...prev, coa_received: e.target.checked }))}
                            />
                          }
                          label="CoA Received"
                        />
                      </Grid>
                    )}
                    <Grid item xs={12} md={4}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formData.sampling_done}
                            onChange={(e) => setFormData(prev => ({ ...prev, sampling_done: e.target.checked }))}
                          />
                        }
                        label="Sampling Done"
                      />
                    </Grid>

                    {/* Additional Notes Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Additional Notes
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label="Remarks (Deviations/Observations)"
                        value={formData.remarks}
                        onChange={(e) => setFormData(prev => ({ ...prev, remarks: e.target.value }))}
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                      />
                    </Grid>

                    {/* Documents Upload Section */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 1,
                        mt: 2,
                        fontSize: '1.1rem'
                      }}>
                        Documents
                      </Typography>
                      <Divider sx={{ mb: 2 }} />
                    </Grid>

                    {/* Invoice Document */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>Invoice</Typography>
                      {formData.invoice_file_name ? (
                        <Card sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formData.invoice_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.invoice_file_size ? `${(formData.invoice_file_size / 1024).toFixed(2)} KB` : ''}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => viewInvoiceFile(formData.invoice_file_path, formData.invoice_file_name)}><VisibilityIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => removeDocumentFile('invoice')} disabled={!canDelete}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </Card>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small">
                          Upload Invoice
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileUpload(e, 'invoice')} />
                        </Button>
                      )}
                      {invoiceFile && !formData.invoice_file_name && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                          {invoiceFile.name} - Ready
                        </Typography>
                      )}
                    </Grid>

                    {/* PO Document */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>Purchase Order</Typography>
                      {formData.po_file_name ? (
                        <Card sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formData.po_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.po_file_size ? `${(formData.po_file_size / 1024).toFixed(2)} KB` : ''}
                          </Typography>
                        </Box>
                            <IconButton size="small" onClick={() => viewInvoiceFile(formData.po_file_path, formData.po_file_name)}><VisibilityIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => removeDocumentFile('po')} disabled={!canDelete}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </Card>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small">
                          Upload PO
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileUpload(e, 'po')} />
                        </Button>
                      )}
                      {poFile && !formData.po_file_name && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                          {poFile.name} - Ready
                        </Typography>
                      )}
                    </Grid>

                    {/* COA Document */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>Certificate of Analysis</Typography>
                      {formData.coa_file_name ? (
                        <Card sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formData.coa_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.coa_file_size ? `${(formData.coa_file_size / 1024).toFixed(2)} KB` : ''}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => viewInvoiceFile(formData.coa_file_path, formData.coa_file_name)}><VisibilityIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => removeDocumentFile('coa')} disabled={!canDelete}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </Card>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small">
                          Upload COA
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileUpload(e, 'coa')} />
                        </Button>
                      )}
                      {coaFile && !formData.coa_file_name && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                          {coaFile.name} - Ready
                        </Typography>
                      )}
                    </Grid>

                    {/* MSDS Document */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>Material Safety Data Sheet</Typography>
                      {formData.msds_file_name ? (
                        <Card sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formData.msds_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.msds_file_size ? `${(formData.msds_file_size / 1024).toFixed(2)} KB` : ''}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => viewInvoiceFile(formData.msds_file_path, formData.msds_file_name)}><VisibilityIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => removeDocumentFile('msds')} disabled={!canDelete}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </Card>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small">
                          Upload MSDS
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileUpload(e, 'msds')} />
                        </Button>
                      )}
                      {msdsFile && !formData.msds_file_name && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                          {msdsFile.name} - Ready
                        </Typography>
                      )}
                    </Grid>

                    {/* BOL Document */}
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" sx={{ fontWeight: 500, mb: 1, display: 'block' }}>Bill of Lading</Typography>
                      {formData.bol_file_name ? (
                        <Card sx={{ p: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <DescriptionIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.85rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {formData.bol_file_name}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {formData.bol_file_size ? `${(formData.bol_file_size / 1024).toFixed(2)} KB` : ''}
                              </Typography>
                            </Box>
                            <IconButton size="small" onClick={() => viewInvoiceFile(formData.bol_file_path, formData.bol_file_name)}><VisibilityIcon fontSize="small" /></IconButton>
                            <IconButton size="small" color="error" onClick={() => removeDocumentFile('bol')} disabled={!canDelete}><DeleteIcon fontSize="small" /></IconButton>
                          </Box>
                        </Card>
                      ) : (
                        <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} fullWidth size="small">
                          Upload BOL
                          <input type="file" hidden accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={(e) => handleFileUpload(e, 'bol')} />
                        </Button>
                      )}
                      {bolFile && !formData.bol_file_name && (
                        <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
                          {bolFile.name} - Ready
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleCloseDialog}>Cancel</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting || uploadingInvoice}>
                  {uploadingInvoice ? 'Uploading...' : submitting ? 'Saving...' : editMode ? 'Update' : 'Add'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
              <DialogTitle>Confirm Delete</DialogTitle>
              <DialogContent>
                <Typography>Are you sure you want to delete this import record?</Typography>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
                <Button onClick={handleConfirmDelete} variant="contained" color="error">
                  Delete
                </Button>
              </DialogActions>
            </Dialog>

            {/* View Details Dialog */}
            <Dialog
              open={viewDialogOpen}
              onClose={() => setViewDialogOpen(false)}
              maxWidth="md"
              fullWidth
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <VisibilityIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Import Record Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Serial No: {viewImport?.serial_number}
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setViewDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ mt: 3 }}>
                {viewImport && (
                  <Grid container spacing={3}>
                    {/* General Information */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        General Information
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Date of Receipt
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {formatDate(viewImport.date_of_receipt)}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Purchase Order No.
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.purchase_order_no || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Invoice Number
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.invoice_number || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Supplier/Vendor Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.supplier_vendor_name || '—'}
                      </Typography>
                    </Grid>

                    {/* Material Details */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        mt: 2,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        Material Details
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Material Name
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.material_name || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Unit of Measure
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.unit_of_measure || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Material Description
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.material_description || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Batch No.
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.batch_no || '—'}
                      </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Lot No.
                      </Typography>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {viewImport.lot_no || '—'}
                      </Typography>
                    </Grid>
                    
                    {/* Storage Location - Only show for raw_materials and products here */}
                    {(viewImport.category === 'raw_materials' || viewImport.category === 'products') && (
                      <Grid item xs={12}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                          Storage Location
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          {viewImport.location || '—'}
                        </Typography>
                      </Grid>
                    )}

                    {/* Dates - Only for raw_materials and products */}
                    {(viewImport.category === 'raw_materials' || viewImport.category === 'products') && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            Dates & Expiry
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Manufacturing Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(viewImport.manufacturing_date)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Expiry Date
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(viewImport.expiry_date)}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {/* Quantity Information */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        mt: 2,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        Quantity Information
                      </Typography>
                    </Grid>
                    
                    {/* Show detailed view for raw_materials and products */}
                    {(viewImport.category === 'raw_materials' || viewImport.category === 'products') ? (
                      <>
                        {viewImport.container_type && (
                          <>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                Container Type
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {viewImport.container_type}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                Number of Containers
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {viewImport.container_count || '—'}
                              </Typography>
                            </Grid>
                            <Grid item xs={12} md={4}>
                              <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                Unit of Measure
                              </Typography>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {viewImport.unit_of_measure}
                              </Typography>
                            </Grid>
                          </>
                        )}
                        
                        {!viewImport.container_type && (
                          <Grid item xs={12} md={4}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                              Unit of Measure
                            </Typography>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              {viewImport.unit_of_measure || '—'}
                            </Typography>
                          </Grid>
                        )}
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Total Quantity Ordered
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {formatNumber(viewImport.quantity_ordered, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Total Quantity Received
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatNumber(viewImport.quantity_received, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Balance Quantity
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'primary.main' }}>
                            {formatNumber(viewImport.balance_quantity, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Qty Issued to Production
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatNumber(viewImport.qty_issued_to_production, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Date of Issue
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(viewImport.date_of_issue)}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={4}>
                          {/* Empty space for alignment */}
                        </Grid>
                      </>
                    ) : (
                      <>
                        {/* Simplified view for caps, bottles, labels, supplies */}
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Quantity Ordered
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'text.primary' }}>
                            {formatNumber(viewImport.quantity_ordered, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Quantity Received
                          </Typography>
                          <Typography variant="h6" sx={{ fontWeight: 500, color: 'success.main' }}>
                            {formatNumber(viewImport.quantity_received, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Storage Location
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {viewImport.location || '—'}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Qty Issued to Production
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatNumber(viewImport.qty_issued_to_production, viewImport.category)} {viewImport.unit_of_measure}
                          </Typography>
                        </Grid>
                        
                        <Grid item xs={12} md={6}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Date of Issue
                          </Typography>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {formatDate(viewImport.date_of_issue)}
                          </Typography>
                        </Grid>
                      </>
                    )}

                    {/* Quality Control */}
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" sx={{ 
                        fontWeight: 500, 
                        color: 'primary.main',
                        mb: 2,
                        pb: 1,
                        mt: 2,
                        borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                      }}>
                        Quality Control
                      </Typography>
                    </Grid>
                    
                    {(viewImport.category === 'raw_materials' || viewImport.category === 'products') && (
                      <Grid item xs={12} md={4}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                          CoA Received
                        </Typography>
                        <Chip 
                          label={viewImport.coa_received ? 'Yes' : 'No'} 
                          color={viewImport.coa_received ? 'success' : 'default'} 
                          size="small" 
                        />
                      </Grid>
                    )}
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Sampling Done
                      </Typography>
                      <Chip 
                        label={viewImport.sampling_done ? 'Yes' : 'No'} 
                        color={viewImport.sampling_done ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Grid>
                    
                    <Grid item xs={12} md={4}>
                      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                        Label Status
                      </Typography>
                      <Chip 
                        label={viewImport.label_status === 'approved' ? 'Approved' : viewImport.label_status === 'rejected' ? 'Rejected' : 'Pending'} 
                        color={viewImport.label_status === 'approved' ? 'success' : viewImport.label_status === 'rejected' ? 'error' : 'default'} 
                        size="small" 
                      />
                    </Grid>

                    {/* Remarks */}
                    {viewImport.remarks && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            Additional Notes
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                            Remarks (Deviations/Observations)
                          </Typography>
                          <Paper sx={{ p: 2, bgcolor: alpha(theme.palette.grey[100], 0.5) }}>
                            <Typography variant="body1">
                              {viewImport.remarks}
                            </Typography>
                          </Paper>
                        </Grid>
                      </>
                    )}

                    {/* Documents */}
                    {(viewImport.invoice_file_name || viewImport.po_file_name || viewImport.coa_file_name || viewImport.msds_file_name || viewImport.bol_file_name) && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'primary.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`
                          }}>
                            Documents
                          </Typography>
                        </Grid>
                        
                        {viewImport.invoice_file_name && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                              Invoice
                            </Typography>
                          <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                              <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {viewImport.invoice_file_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {viewImport.invoice_file_size ? `${(viewImport.invoice_file_size / 1024).toFixed(2)} KB` : ''}
                                </Typography>
                              </Box>
                              <Button
                                variant="contained"
                                size="small"
                                startIcon={<VisibilityIcon />}
                                onClick={() => viewInvoiceFile(viewImport.invoice_file_path, viewImport.invoice_file_name)}
                              >
                                View
                              </Button>
                            </Box>
                          </Card>
                        </Grid>
                        )}
                        
                        {viewImport.po_file_name && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                              Purchase Order
                            </Typography>
                            <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {viewImport.po_file_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {viewImport.po_file_size ? `${(viewImport.po_file_size / 1024).toFixed(2)} KB` : ''}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => viewInvoiceFile(viewImport.po_file_path, viewImport.po_file_name)}
                                >
                                  View
                                </Button>
                              </Box>
                            </Card>
                          </Grid>
                        )}
                        
                        {viewImport.coa_file_name && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                              Certificate of Analysis
                            </Typography>
                            <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {viewImport.coa_file_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {viewImport.coa_file_size ? `${(viewImport.coa_file_size / 1024).toFixed(2)} KB` : ''}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => viewInvoiceFile(viewImport.coa_file_path, viewImport.coa_file_name)}
                                >
                                  View
                                </Button>
                              </Box>
                            </Card>
                          </Grid>
                        )}
                        
                        {viewImport.msds_file_name && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                              Material Safety Data Sheet
                            </Typography>
                            <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {viewImport.msds_file_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {viewImport.msds_file_size ? `${(viewImport.msds_file_size / 1024).toFixed(2)} KB` : ''}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => viewInvoiceFile(viewImport.msds_file_path, viewImport.msds_file_name)}
                                >
                                  View
                                </Button>
                              </Box>
                            </Card>
                          </Grid>
                        )}
                        
                        {viewImport.bol_file_name && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 1 }}>
                              Bill of Lading
                            </Typography>
                            <Card sx={{ p: 2, bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <DescriptionIcon sx={{ color: 'primary.main', fontSize: 32 }} />
                                <Box sx={{ flex: 1 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                    {viewImport.bol_file_name}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {viewImport.bol_file_size ? `${(viewImport.bol_file_size / 1024).toFixed(2)} KB` : ''}
                                  </Typography>
                                </Box>
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<VisibilityIcon />}
                                  onClick={() => viewInvoiceFile(viewImport.bol_file_path, viewImport.bol_file_name)}
                                >
                                  View
                                </Button>
                              </Box>
                            </Card>
                          </Grid>
                        )}
                      </>
                    )}

                    {/* Approval Information */}
                    {viewImport.status === 'approved' && viewImport.approved_by_signature && (
                      <>
                        <Grid item xs={12}>
                          <Typography variant="subtitle1" sx={{ 
                            fontWeight: 500, 
                            color: 'success.main',
                            mb: 2,
                            pb: 1,
                            mt: 2,
                            borderBottom: `2px solid ${alpha(theme.palette.success.main, 0.2)}`
                          }}>
                            Approval Information
                          </Typography>
                        </Grid>
                        <Grid item xs={12}>
                          <Card sx={{ 
                            p: 3, 
                            border: `1px solid ${theme.palette.success.main}`, 
                            borderRadius: 2, 
                            backgroundColor: alpha(theme.palette.success.main, 0.05)
                          }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 500, mb: 2, color: 'success.main', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <VerifiedIcon />
                              Approved By
                            </Typography>
                            <Box sx={{ 
                              border: `1px solid ${theme.palette.success.main}`, 
                              p: 2, 
                              borderRadius: 1, 
                              bgcolor: 'white',
                              mb: 2
                            }}>
                              <Box sx={{ 
                                fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                fontSize: '24px', 
                                color: theme.palette.text.primary
                              }}>
                                {viewImport.approved_by_signature}
                              </Box>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                              Date: {formatDate(viewImport.approved_at)}
                            </Typography>
                            {viewImport.approval_notes && (
                              <Box sx={{ mt: 2 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, display: 'block', mb: 0.5 }}>
                                  Approval Notes:
                                </Typography>
                                <Typography variant="body2">{viewImport.approval_notes}</Typography>
                              </Box>
                            )}
                          </Card>
                        </Grid>
                      </>
                    )}
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 3, gap: 1 }}>
                <Button onClick={() => setViewDialogOpen(false)} variant="outlined">
                  Close
                </Button>
                {canEdit && (
                  <Button 
                    onClick={() => {
                      setViewDialogOpen(false);
                      handleOpenDialog(viewImport);
                    }}
                    variant="contained"
                    startIcon={<EditIcon />}
                  >
                    Edit Record
                  </Button>
                )}
              </DialogActions>
            </Dialog>

            {/* Approval Dialog */}
            <Dialog 
              open={approvalDialogOpen} 
              onClose={() => setApprovalDialogOpen(false)}
              maxWidth="sm"
              fullWidth
              disableScrollLock={true}
              PaperProps={{
                sx: {
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`
                }
              }}
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                fontSize: '1.2rem',
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: alpha(theme.palette.primary.main, 0.05),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                p: 3
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CheckCircleIcon sx={{ color: 'success.main' }} />
                  <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
                    Approve Import Record
                  </Typography>
                </Box>
                <IconButton 
                  onClick={() => setApprovalDialogOpen(false)} 
                  size="small"
                  sx={{
                    borderRadius: 2,
                    border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                    '&:hover': {
                      transform: 'scale(1.1)',
                      border: `1px solid ${theme.palette.primary.main}`,
                      boxShadow: `0 2px 8px ${alpha(theme.palette.grey[900], 0.2)}`
                    }
                  }}
                >
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                backgroundColor: alpha(theme.palette.grey[50], 0.5), 
                p: 3,
                maxHeight: '70vh', 
                overflowY: 'auto',
                mt: 4
              }}>
                {approvalImport && (
                  <Grid container spacing={3}>
                    {/* Signature Section */}
                    <Grid item xs={12}>
                      <Card sx={{ 
                        p: 3, 
                        border: `1px solid ${theme.palette.grey[400]}`, 
                        borderRadius: 3, 
                        backgroundColor: 'white',
                        boxShadow: `0 4px 20px ${alpha(theme.palette.primary.main, 0.08)}`
                      }}>
                        <Typography variant="h6" sx={{ fontWeight: 500, mb: 2, color: theme.palette.text.primary }}>
                          Approval Signature
                        </Typography>
                        <Grid container spacing={3}>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Your Name (Electronic Signature) *"
                              value={signatureName}
                              onChange={(e) => setSignatureName(e.target.value)}
                              required
                              placeholder="Enter your full name to approve this record"
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                          
                          {signatureName && (
                            <Grid item xs={12}>
                              <Box sx={{ 
                                border: `1px solid ${theme.palette.primary.main}`, 
                                p: 3, 
                                borderRadius: 2, 
                                bgcolor: alpha(theme.palette.primary.main, 0.05)
                              }}>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, fontWeight: 500 }}>
                                  ELECTRONIC SIGNATURE PREVIEW:
                                </Typography>
                                <Box sx={{ 
                                  fontFamily: "'Brush Script MT', 'Brush Script Std', cursive", 
                                  fontSize: '24px', 
                                  color: theme.palette.text.primary,
                                  textShadow: '1px 1px 1px rgba(0,0,0,0.1)',
                                  mb: 1
                                }}>
                                  {signatureName}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                                  Signed: {dayjs().format('MMM DD, YYYY HH:mm')}
                                </Typography>
                              </Box>
                            </Grid>
                          )}

                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Approval Notes (Optional)"
                              value={approvalNotes}
                              onChange={(e) => setApprovalNotes(e.target.value)}
                              placeholder="Add any notes or comments about this approval..."
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              </DialogContent>
              
              <DialogActions sx={{ 
                p: 3, 
                gap: 2, 
                borderTop: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`
              }}>
                <Button 
                  onClick={() => setApprovalDialogOpen(false)}
                  startIcon={<CloseIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 3,
                    py: 1.5,
                    color: theme.palette.text.secondary,
                    fontWeight: 500,
                    background: 'rgba(255, 255, 255, 0.8)',
                    backdropFilter: 'blur(10px)',
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.04)
                    }
                  }}
                >
                  Cancel
                </Button>
                
                <Button 
                  onClick={handleApprovalSubmit}
                  variant="contained"
                  startIcon={<CheckCircleIcon />}
                  disabled={!signatureName.trim()}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                    color: 'white',
                    fontWeight: 500,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.3)}`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`,
                      boxShadow: `0 6px 20px ${alpha(theme.palette.success.main, 0.4)}`
                    },
                    '&:disabled': {
                      background: theme.palette.grey[400],
                      color: theme.palette.grey[100]
                    }
                  }}
                >
                  Approve
                </Button>
              </DialogActions>
            </Dialog>

            {/* Transaction Selection Dialog (for Chemicals autofill) */}
            <Dialog
              open={transactionDialogOpen}
              onClose={() => setTransactionDialogOpen(false)}
              maxWidth="lg"
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{ 
                fontWeight: 500,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <InputIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Select Chemical Transaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose a transaction to autofill the import record
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setTransactionDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                mt: 2, 
                p: 3,
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search transactions..."
                    value={transactionSearchTerm}
                    onChange={(e) => setTransactionSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Box>

                {loadingTransactions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%',
                    borderRadius: 2
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>PO #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Invoice #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Material</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {transactionSearchTerm ? 'No transactions found matching your search' : 'No chemical purchase transactions found'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredTransactions.map((txn) => (
                            <TableRow 
                              key={txn.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                              }}
                              onClick={() => handleSelectTransaction(txn)}
                            >
                              <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.po_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.invoice_number || '—'}
                                </Typography>
                                {txn.reference_number && (
                                  <Typography variant="caption" color="text.secondary">
                                    Ref: {txn.reference_number}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.raw_material?.name || '—'}
                                </Typography>
                                {(txn.batch_number || txn.lot_number) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {txn.batch_number && `Batch: ${txn.batch_number}`}
                                    {txn.batch_number && txn.lot_number && ' | '}
                                    {txn.lot_number && `Lot: ${txn.lot_number}`}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{txn.supplier?.name || '—'}</TableCell>
                              <TableCell>
                                <Typography variant="body2">
                                  {formatNumber(Math.abs(txn.unit_quantity))} {txn.raw_material?.primary_unit_type}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ({formatNumber(Math.abs(txn.total_quantity))} {txn.raw_material?.primary_unit_measure})
                                </Typography>
                              </TableCell>
                              <TableCell>{txn.location?.name || '—'}</TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectTransaction(txn);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setTransactionDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Caps Transaction Selection Dialog */}
            <Dialog 
              open={capsTransactionDialogOpen} 
              onClose={() => setCapsTransactionDialogOpen(false)} 
              maxWidth="lg" 
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{
                py: 2.5,
                px: 3,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <InputIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Select Caps Transaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose a transaction to autofill the import record
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setCapsTransactionDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                mt: 2, 
                p: 3,
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search transactions..."
                    value={capsTransactionSearchTerm}
                    onChange={(e) => setCapsTransactionSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Box>

                {loadingCapsTransactions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%',
                    borderRadius: 2
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>PO #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Invoice #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Cap</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredCapsTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {capsTransactionSearchTerm ? 'No transactions found matching your search' : 'No caps purchase transactions found'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredCapsTransactions.map((txn) => (
                            <TableRow 
                              key={txn.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                              }}
                              onClick={() => handleSelectCapsTransaction(txn)}
                            >
                              <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.po_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.invoice_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.cap?.name || '—'}
                                </Typography>
                                {(txn.batch_number || txn.lot_number) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {txn.batch_number && `Batch: ${txn.batch_number}`}
                                    {txn.batch_number && txn.lot_number && ' | '}
                                    {txn.lot_number && `Lot: ${txn.lot_number}`}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{txn.supplier?.name || '—'}</TableCell>
                              <TableCell>{formatNumber(Math.abs(txn.quantity))}</TableCell>
                              <TableCell>{txn.location?.name || '—'}</TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectCapsTransaction(txn);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setCapsTransactionDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Bottles Transaction Selection Dialog */}
            <Dialog 
              open={bottlesTransactionDialogOpen} 
              onClose={() => setBottlesTransactionDialogOpen(false)} 
              maxWidth="lg" 
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{
                py: 2.5,
                px: 3,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <InputIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Select Bottles Transaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose a transaction to autofill the import record
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setBottlesTransactionDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                mt: 2, 
                p: 3,
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search transactions..."
                    value={bottlesTransactionSearchTerm}
                    onChange={(e) => setBottlesTransactionSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Box>

                {loadingBottlesTransactions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%',
                    borderRadius: 2
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>PO #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Invoice #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Bottle</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredBottlesTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {bottlesTransactionSearchTerm ? 'No transactions found matching your search' : 'No bottles purchase transactions found'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredBottlesTransactions.map((txn) => (
                            <TableRow 
                              key={txn.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                              }}
                              onClick={() => handleSelectBottlesTransaction(txn)}
                            >
                              <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.po_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.invoice_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.bottle?.name || '—'}
                                </Typography>
                                {(txn.batch_number || txn.lot_number) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {txn.batch_number && `Batch: ${txn.batch_number}`}
                                    {txn.batch_number && txn.lot_number && ' | '}
                                    {txn.lot_number && `Lot: ${txn.lot_number}`}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{txn.supplier?.name || '—'}</TableCell>
                              <TableCell>{formatNumber(Math.abs(txn.quantity))}</TableCell>
                              <TableCell>{txn.location?.name || '—'}</TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectBottlesTransaction(txn);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setBottlesTransactionDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Labels Transaction Selection Dialog */}
            <Dialog 
              open={labelsTransactionDialogOpen} 
              onClose={() => setLabelsTransactionDialogOpen(false)} 
              maxWidth="lg" 
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{
                py: 2.5,
                px: 3,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <InputIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Select Labels Transaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose a transaction to autofill the import record
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setLabelsTransactionDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                mt: 2, 
                p: 3,
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search transactions..."
                    value={labelsTransactionSearchTerm}
                    onChange={(e) => setLabelsTransactionSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Box>

                {loadingLabelsTransactions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%',
                    borderRadius: 2
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>PO #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Invoice #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Label</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredLabelsTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {labelsTransactionSearchTerm ? 'No transactions found matching your search' : 'No labels purchase transactions found'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredLabelsTransactions.map((txn) => (
                            <TableRow 
                              key={txn.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                              }}
                              onClick={() => handleSelectLabelsTransaction(txn)}
                            >
                              <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.po_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.invoice_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.label?.name || '—'}
                                </Typography>
                                {(txn.batch_number || txn.lot_number) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {txn.batch_number && `Batch: ${txn.batch_number}`}
                                    {txn.batch_number && txn.lot_number && ' | '}
                                    {txn.lot_number && `Lot: ${txn.lot_number}`}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{txn.supplier?.name || '—'}</TableCell>
                              <TableCell>{formatNumber(Math.abs(txn.quantity))}</TableCell>
                              <TableCell>{txn.location?.name || '—'}</TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectLabelsTransaction(txn);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setLabelsTransactionDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Supplies Transaction Selection Dialog */}
            <Dialog 
              open={suppliesTransactionDialogOpen} 
              onClose={() => setSuppliesTransactionDialogOpen(false)} 
              maxWidth="lg" 
              fullWidth
              PaperProps={{
                sx: {
                  height: '95vh',
                  maxHeight: '95vh'
                }
              }}
            >
              <DialogTitle sx={{
                py: 2.5,
                px: 3,
                color: 'primary.main',
                borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.03)})`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexShrink: 0
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <InputIcon sx={{ color: 'primary.main' }} />
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      Select Supplies Transaction
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Choose a transaction to autofill the import record
                    </Typography>
                  </Box>
                </Box>
                <IconButton onClick={() => setSuppliesTransactionDialogOpen(false)} size="small">
                  <CloseIcon />
                </IconButton>
              </DialogTitle>
              
              <DialogContent sx={{ 
                mt: 2, 
                p: 3,
                display: 'flex', 
                flexDirection: 'column',
                overflow: 'hidden',
                flex: '1 1 auto',
                minHeight: 0
              }}>
                <Box sx={{ mb: 3, flexShrink: 0 }}>
                  <TextField
                    placeholder="Search transactions..."
                    value={suppliesTransactionSearchTerm}
                    onChange={(e) => setSuppliesTransactionSearchTerm(e.target.value)}
                    size="small"
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon sx={{ color: theme.palette.primary.main, fontSize: 20 }} />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 3,
                        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                        background: 'white',
                        '& fieldset': { border: 'none' },
                        '&:hover': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.15)}`
                        },
                        '&.Mui-focused': {
                          border: `1px solid ${theme.palette.primary.main}`,
                          boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.25)}`
                        }
                      }
                    }}
                  />
                </Box>

                {loadingSuppliesTransactions ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <LinearProgress sx={{ width: '50%' }} />
                  </Box>
                ) : (
                  <TableContainer component={Paper} sx={{ 
                    flex: '1 1 auto',
                    overflow: 'auto',
                    minHeight: 0,
                    maxHeight: '100%',
                    borderRadius: 2
                  }}>
                    <Table stickyHeader size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>PO #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Invoice #</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Supply</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Supplier</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Quantity</TableCell>
                          <TableCell sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Location</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 500, bgcolor: 'grey.100' }}>Action</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredSuppliesTransactions.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">
                                {suppliesTransactionSearchTerm ? 'No transactions found matching your search' : 'No supplies purchase transactions found'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ) : (
                          filteredSuppliesTransactions.map((txn) => (
                            <TableRow 
                              key={txn.id}
                              hover
                              sx={{ 
                                cursor: 'pointer',
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.05) }
                              }}
                              onClick={() => handleSelectSuppliesTransaction(txn)}
                            >
                              <TableCell>{formatDate(txn.transaction_date)}</TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.po_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.invoice_number || '—'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {txn.supply?.name || '—'}
                                </Typography>
                                {(txn.batch_number || txn.lot_number) && (
                                  <Typography variant="caption" color="text.secondary">
                                    {txn.batch_number && `Batch: ${txn.batch_number}`}
                                    {txn.batch_number && txn.lot_number && ' | '}
                                    {txn.lot_number && `Lot: ${txn.lot_number}`}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>{txn.supplier?.name || '—'}</TableCell>
                              <TableCell>{formatNumber(Math.abs(txn.quantity))}</TableCell>
                              <TableCell>{txn.location?.name || '—'}</TableCell>
                              <TableCell align="center">
                                <Button
                                  variant="contained"
                                  size="small"
                                  startIcon={<CheckIcon />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleSelectSuppliesTransaction(txn);
                                  }}
                                >
                                  Select
                                </Button>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </DialogContent>
              
              <DialogActions sx={{ p: 2, flexShrink: 0 }}>
                <Button onClick={() => setSuppliesTransactionDialogOpen(false)} variant="outlined">
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Snackbar */}
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
              <Alert severity={snackbar.severity} onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}>
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Container>
        </Box>
      </Fade>
    </LocalizationProvider>
  );
};

export default ImportsPage;